// ══════════════════════════════════════════════════════════════════
//  물맷돌 부품 — **카드·중첩·갈래·결·진화를 전면 대체한다**
//  (설계 정본: `아흔아홉_물맷돌강화트리_설계안.md`)
//
//  ■ 왜 갈아엎었나 — **히트작의 공통점은 장치가 아니라 「칸이 모자란 것」이다.**
//    VS 무기 6 + 패시브 6 / Brotato 무기 6 + 돈 / DRG 무기 4 · 오버클럭 3회 /
//    DMD 신 4명 — 전부 갖고 싶은 것보다 자리가 적다.
//    우리는 **활성 13장 = 자리 13개**라 남지도 모자라지도 않았다. v1 0-1이 이미
//    계산해 뒀다: 밤 끝 기대 중첩 합 37.4 · 구매 상한 40.5 · 실측 구매/희망 100%.
//    → **12분 동안 포기한 것이 0이다.** 그것이 「판이 매번 같다」의 정체였다.
//
//  ■ 그리고 카드 중첩은 **읽히지 않았다.** 「무거운 돌」 3중첩이 무슨 뜻인가 —
//    돌이 3배 무거워지나, 무거운 돌을 3개 던지나. 물리적으로 읽히는 그림이 없다.
//    **부품이면 즉시 읽힌다** — 무거운 돌로 **바꿔 낀** 것이다.
//
//  ■ **두 축이 따로 논다. 이것이 이 파일의 중심이다.**
//      급(cls)   = 얼마나 단단히 무나 ← 이산. 낮은 급도 끼되 좋은 축이 덜 전달된다
//      등급(tier)= 얼마나 잘 하나   ← 연속. **이것으로는 절대 안 막는다**
//    무거운 돌도 얇은 가죽에 물릴 수 있다. 다만 급 차이만큼 물림·부품 이득이 줄어든다.
//
// ══════════════════════════════════════════════════════════════════
//  ■■ 【재설계 2026-07-30】 **여섯이 여섯 가지 다른 질문에 답한다** ■■
//
//  실플레이 진단: **고른 돌 · 강가 돌 · 차돌 셋이 「포지션이 애매하다」**.
//  원인을 찾아보니 **여섯이 전부 「어떻게 많이 맞추나」 하나의 질문에 답하고 있었다.**
//      잔돌 여러 발 · 강가 한 발이 여럿을 뚫어 · 모난 조각으로 · 차돌 이월로 ·
//      무거운 반경으로 · 고른 (없음)
//  그리고 정확히 **화면에 안 보이는 셋**이 애매하다고 지목됐다 —
//  잔돌(하늘이 빽빽)·모난 돌(조각이 튄다)·무거운 돌(터진다)은 전용 연출이 있는데
//  고른 돌·강가 돌·차돌은 **아무 전용 연출이 없었다.**
//
//  > **정체는 값이 아니라 화면에서 나온다.** 밤 리모델링에서 「강해졌다는 느낌이
//  > 안 든다」의 원인이 「등급이 시각 채널을 하나도 안 건드린 것」이었던 그 자리와 같다.
//
//  → 축을 하나(수)에서 **넷(수·선·거리·시간)**으로 늘린다.
//      마른 잔돌      **수**   여럿이 부채꼴로            (유지)
//      성긴 고른 돌   **선**   무한 관통 + 등급이 발 간격을 좁힌다   (변경)
//      거친 납작한 돌 **거리** 물수제비 — 통통통 튀며 자국을 남긴다  (변경)
//      길든 모난 돌   **방사** 부딪힌 자리에서 조각이 사방으로  (유지)
//      묵은 차돌      **시간** 필드에 남고, 회수하면 더 커져 있다   (변경)
//      굳은 무거운 돌 **반경** 멀리 한 자리가 크게 터진다     (유지)
//
//  ■ **세트가 생겼다.** 수식어 6 × 부위 4 = 24. 각 칸에 정확히 하나.
//    같은 수식어를 4칸에 다 맞추고 최저 등급이 4 이상이면 그 세트의
//    정체가 극단으로 밀린다. **새 개념은 하나도 안 만든다.**
// ══════════════════════════════════════════════════════════════════
import { UNIT } from './config.js';

// ── 급 ────────────────────────────────────────────────────────────
// 모든 급을 서로 낄 수 있다. 급 차이는 장착 금지가 아니라 아래 `classGrip`의
// 돌 피해 전달로 번역한다. 같은 급·상위 급은 기존과 정확히 같은 1이다.
export const CLS = { light: 0, mid: 1, heavy: 2 };
export const CLS_KO = ['가벼움', '보통', '무거움'];

// ── 등급 ──────────────────────────────────────────────────────────
// 일반 제시는 5단계다. 6성은 뽑히지 않고 **같은 5성 부품을 다시 샀을 때만** 열린다.
// 화면에서도 점 여섯이 아니라 다이아 한 점으로 따로 말한다.
// ⚠ 배수 폭 0.6~1.7은 **측정 대기**. 밤 끝 목표 화력(4~6배)이 정해져야 역산된다.
export const TIER_MAX = 5;
export const TIER_DIAMOND = 6;
export const TIER_MUL = [0.6, 0.8, 1.0, 1.3, 1.7, 2.2]; // 마지막은 재구매 전용 ◆
export function tierMul(t) { return TIER_MUL[Math.max(0, Math.min(TIER_DIAMOND - 1, (t | 0) - 1))]; }
const tIdx = (t) => Math.max(0, Math.min(TIER_DIAMOND - 1, (t | 0) - 1));
export function isDiamondTier(t) { return (t | 0) >= TIER_DIAMOND; }

// 부가 줄이 붙는 등급 — ●●●○○·●●●●○ 1줄 / ●●●●● 2줄.
// **확률로 하지 않는다.** 4칸 × 확률이면 최대 12줄을 매번 읽어야 한다.
export function traitCount(t) { return t >= 5 ? 2 : t >= 3 ? 1 : 0; }

// ══════════════════════════════════════════════════════════════════
//  이름 — **전부 여기 한 곳에 있다**
//
//  ⚠ 사장님 원문: *"네이밍은 나중에 바꾸면 되는거잖아, 단순히 이름만 바꾸는거니까."*
//    → 나중에 **통째로 갈 수 있게** 문자열을 한 표에 모은다. 부품 정의에는 `ko` 를
//      안 적고, 파일 끝에서 이 표를 보고 채워 넣는다(`PARTS` 조립부).
//  ⚠ 받침·끈·걸이는 **「수식어 + 부위말」로 합성**한다 — 18개를 따로 안 적는다.
//    돌은 아래 표의 확정 이름을 그대로 쓴다.
// ══════════════════════════════════════════════════════════════════
export const SET_IDS = ['dry', 'loose', 'rough', 'tamed', 'aged', 'hard'];
export const SET_KO = {
  dry: '가벼운', loose: '손에 익은', rough: '매끈한', tamed: '거친', aged: '단단한', hard: '무거운',
};
export const STONE_KO = {
  jandol: '가벼운 돌', goreun: '손에 익은 돌', napjak: '매끈한 돌',
  monan: '거친 돌', chadol: '단단한 돌', mugeo: '무거운 돌',
};
// 부위말 — 「가벼운」+「받침」 = 「가벼운 받침」
export const PART_SUFFIX = { cradle: '받침', cord: '끈', loop: '고리' };
// 세트를 채웠을 때 무슨 일이 나는가 — 화면 문구도 여기서만 나온다
export const SET_FX_KO = {
  dry:   '발이 더 늘어난다',
  loose: '간격이 사라진다 — 진짜 선이 된다',
  rough: '튐이 안 잦아든다',
  tamed: '조각이 다시 조각을 낸다',
  aged:  '도는 원이 두 배가 된다',
  hard:  '터진 자리가 또 터진다',
};
// 네 칸을 모두 ◆로 마친 뒤에만 열리는 세 번째 세트 단계.
// 기존 큰 세트의 `fx`는 계속 살아 있고, 이 문구는 그 위에 더해진
// ◆ 전용 사건만 말한다. UI는 두 문구를 섞어 외우지 않는다.
export const SET_DIAMOND_FX_KO = {
  dry:   '한 줌이 끝내 넘쳐난다',
  loose: '여섯 박자마다 선이 겹쳐 그어진다',
  rough: '멀어진 튐이 다시 무리로 꺾인다',
  tamed: '마지막 조각이 먹잇감으로 되돌아든다',
  aged:  '여섯 번째 원은 안쪽까지 휩쓴다',
  hard:  '마지막 폭발이 바깥 고리로 번진다',
};

// 모든 2+2 조합(15개)을 자동 세트로 만들지 않는다. 아래 여섯 조합만 손으로
// 고른 행동 세트다. 힘은 네 부품의 평균이 아니라 각 쌍의 최저 등급 중 더 낮은
// 쪽에서 오므로, 높은 등급 하나가 낮은 짝을 끌고 갈 수 없다.
export const HYBRID_SETS = Object.freeze([
  { id: 'dry_rough',   ko: '물비늘 부채', fx: '가장자리 돌이 첫 명중 뒤 한 번 꺾인다', hook: 'edgeSeek',
    families: Object.freeze(['dry', 'rough']) },
  { id: 'loose_tamed', ko: '갈라진 직선', fx: '몇 번째 명중마다 앞쪽 조각 하나가 이어진다', hook: 'lineShard',
    families: Object.freeze(['loose', 'tamed']) },
  { id: 'aged_hard',   ko: '울림 고리', fx: '몇 번째 명중마다 작은 충격 고리가 한 번 울린다', hook: 'impactRing',
    families: Object.freeze(['aged', 'hard']) },
  { id: 'dry_tamed',   ko: '엇갈린 살', fx: '발사 묶음이 좌우로 번갈아 어긋난다', hook: 'crossVolley',
    families: Object.freeze(['dry', 'tamed']) },
  { id: 'loose_hard',  ko: '되감긴 궤적', fx: '빗나간 돌이 한 번 되돌아온다', hook: 'returnShot',
    families: Object.freeze(['loose', 'hard']) },
  { id: 'rough_aged',  ko: '감아도는 물결', fx: '첫 충돌 뒤 바깥 먹잇감으로 한 번 꺾인다', hook: 'outerSeek',
    families: Object.freeze(['rough', 'aged']) },
]);
export const HYBRID_SET_BY_ID = Object.freeze(Object.fromEntries(HYBRID_SETS.map(x => [x.id, x])));

// ══════════════════════════════════════════════════════════════════
//  돌 6종 — **무게 하나가 세 곳에 걸린다**
//  무게가 곧 피해이고, 곧 느림이고, 곧 급이다. 숫자 하나로 셋이 정해진다.
//
//  ⚠⚠ **성질값(발 수·튐 횟수·조각 수·성장·반경)은 여기 없다.**
//     전부 아래 `STONE_AXIS`(등급이 무엇을 미는가 표)에 있다. 두 군데에 적으면
//     한쪽만 고쳐지는 사고가 난다 — 이 저장소의 상습 결함이다.
//     여기 남는 것은 **무게·피해·연사·급·세트**와 **구조 플래그** 셋뿐이다:
//       small     받침이 「여러 개 물 수 있는」 돌인가
//       pierceInf 뚫고 최종 사거리 끝까지 가는가 (성긴 고른 돌)
//     ⚠ 구 `keep`(던지면 필드에 남는가)은 **삭제됐다** — 차돌이 근접(궤도)으로
//       재설계되면서 「던지고 주우러 간다」가 통째로 없어졌다(STONE_AXIS.chadol 주석).
// ══════════════════════════════════════════════════════════════════
export const STONES = [
  // ── 마른 잔돌 — **수.** 여럿이 부채꼴로. (유지)
  { id: 'jandol', setId: 'dry',   cls: CLS.light, w: 0.5, dmg: 0.62, rate: 1.30, small: true },

  // ── 성긴 고른 돌 — **선.** 「멈추지 않는다」 (변경)
  //   · **관통 무한.** 적을 뚫고 최종 사거리 끝까지 간다. `range 1.15` 는 유지
  //   · **등급이 발 간격을 좁힌다** — ●○○○○ 는 평범, ●●●●● 는 돌이 촘촘해
  //     **끊긴 선 하나가 화면을 가른다**
  //   ⚠ **빛나는 선이 아니라 돌이 촘촘한 선이어야 한다.** 트레일을 밝히지 마라 —
  //     빛나면 판타지고 돌이 빽빽하면 고증이다
  //   ⚠⚠ **연사는 걸이의 축이다.** 겹치지 않게 **기본 `rate` 를 1.38 → 1.00(평범)으로
  //     내리고**, 극단은 등급에만 맡긴다(`STONE_AXIS.goreun`). 이것이 이 재설계에서
  //     **돌 기본값을 건드린 유일한 자리**다 — 충돌 4의 처방이다.
  //   · 관통이 **강가 돌 → 고른 돌로 이사**했다. `mods.pierceInf` 훅이 이미 있어
  //     값만 옮기면 된다(구 진화 「가르는 선」이 쓰던 자리).
  //   · 피해 0.94 — ●5 손에 익은 한 벌의 한 선이 40일 물량 최대 HP를 넘기는
  //     최소 실전 눈금이다. 무한 관통인데도 첫 줄이 물량을 남기던 0.80을 회복한다.
  { id: 'goreun', setId: 'loose', cls: CLS.light, w: 0.8, dmg: 0.94, rate: 1.00,
    range: 1.15, pierceInf: true },

  // ── 거친 납작한 돌 — **거리.** 「물수제비」 (변경 · 구 「강가 돌」)
  //   · `pierce: 4` **삭제.** 대신 **튐**(STONE_AXIS.napjak = 5/8/12/18/25/32)
  //   · 이름을 「강가 돌」에서 바꿨다 — **물수제비를 뜨는 돌은 납작하다**
  //   · ⚠ `dmg 1.00 → 1.50` — **사장님 지시 2026-07-30 3차**: *"지금도 좋은데 여기서
  //     좀 더 상향하자고. **성능 1.5배** 정도."*  자국 하나하나가 이 값을 쓰므로 튐 전체가 1.5배다.
  { id: 'napjak', setId: 'rough', cls: CLS.mid,   w: 1.0, dmg: 1.50, rate: 1.00 },

  // ── 길든 모난 돌 — **방사.** 부딪힌 자리에서 조각이 사방으로. (유지)
  { id: 'monan',  setId: 'tamed', cls: CLS.mid,   w: 1.0, dmg: 1.00, rate: 1.00, range: 0.85 },

  // ── 묵은 차돌 — **시간.** 「하나를 키운다」 (변경)
  //     던진다 → 부순다 → 바닥에 남는다 → 지나가면 회수된다 → 더 커져 있다 → 다시 던진다
  //                                     ↑ 그동안은 궤도(도는 돌)가 지킨다
  //   · `pierce: 3` · `ramp: 0.25` · `carry: true` **전부 삭제**(충돌 2·A-3)
  //   · **개수는 받침이 정한다** — 묵은 받침만 `hold: 2`(아래 C-1). 두 개가 각각 자라고
  //     각각 회수해야 한다
  //   · **밤이 끝나면 리셋.** 성장은 저장하지 않는다(충돌 7) — 그래서 이 파일에
  //     상태가 없다. `resolveGear` 는 「얼마나 자랄 수 있나」만 낸다
  // ⚠ `melee` — **이 돌은 던지지 않는다.** 투사체가 0 이고 몸 옆에서 도는 것이 전부다
  //   (사장님 지시 2026-07-30: *"던지는게 아니라 그 물멧돌 돌리는 범위 자체가
  //   롤에 가렌처럼 빙글빙글 도는 그 범위에 딜이 지속적으로 들어가게"*).
  { id: 'chadol', setId: 'aged',  cls: CLS.heavy, w: 1.5, dmg: 1.40, rate: 0.90, melee: true },

  // ── 굳은 무거운 돌 — **반경.** 멀리 한 자리가 크게 터진다. (유지)
  { id: 'mugeo',  setId: 'hard',  cls: CLS.heavy, w: 2.0, dmg: 1.80, rate: 0.80 },
];

// ══════════════════════════════════════════════════════════════════
//  ■■ 등급이 **무엇을** 미는가 — 돌마다 다르다 ■■   【충돌 1 · 이번 작업의 핵심】
//
//  구 구현은 `TIER_MUL` 이 **모든 값에 일괄**로 곱해졌다. 그런데 여섯의 축이 갈라진
//  지금은 그것이 성립하지 않는다 — 납작한 돌은 **튐 횟수**, 고른 돌은 **발 간격**,
//  차돌은 **성장**을 밀어야 한다. 그래서 「돌 → 축」 표를 따로 세운다.
//
//  ⚠⚠ **여기 숫자를 지어내지 않았다.** 세 가지 출처뿐이다:
//    ① 정본        — 납작한 돌 5/8/12/18/25 + ◆ 32 (사장님 확정)
//    ② 기존 값 승계 — 잔돌 shots 2 · 모난 조각 8 · 무거운 반경 3.2 에
//                    **구 공식(`값 × tierMul`)을 그대로** 태운 결과. 값이 안 바뀐다
//    ③ 등급 숫자 자체 — 차돌 성장 단계 = 등급(1~6)
//  고른 돌만 새 곡선인데, 그것도 **기존 값 1.38 을 지수만 제곱해서** 얻었다:
//    구 `1.38^tierMul` → 새 `1.38^(tierMul²)`. ●●●○○ 에서 정확히 구 값과 같고
//    양끝만 벌어진다 = *"기본 rate 는 평범하게, 등급에서만 극단으로"*.
// ══════════════════════════════════════════════════════════════════
const byMul   = (base) => TIER_MUL.map(m => base * m);                 // 구 공식 그대로
const byRound = (base) => TIER_MUL.map(m => Math.round(base * m));     // 개수는 정수로
const byPow2  = (base) => TIER_MUL.map(m => Math.pow(base, m * m));    // 등급만 극단

export const STONE_AXIS = {
  // 잔돌 — **수.** 구 `shots: 2` × tierMul 반올림 = [1,2,2,3,3]. 값이 안 바뀐다
  jandol: { key: 'shots',  ko: '발 수',       v: byRound(2) },
  // 고른 돌 — **선.** 축은 연사이고 그 결과가 「발 간격」이다.
  //   간격 = 투사체 속도 ÷ 초당 발수 이므로 **연사가 곧 간격**이다. gear 는 속도를
  //   모르므로 `lineGap`(간격 배율 = 1/축)로 내보내고 scene 이 그것으로 돌을 깐다.
  //   [1.12, 1.23, 1.38, 1.72, 2.54] — ●●●○○ 가 구 값 1.38 과 같다
  goreun: { key: 'rate',   ko: '발 간격',     v: byPow2(1.38) },
  // 납작한 돌 — **거리.** 【정본】 사장님 확정 수열
  napjak: { key: 'skips',  ko: '튐 횟수',     v: [5, 8, 12, 18, 25, 32] },
  // 모난 돌 — **방사.** 구 `shards: 8` × tierMul. 값이 안 바뀐다
  monan:  { key: 'shards', ko: '조각 수',     v: byMul(8) },
  // 차돌 — **근접.** 「도는 돌」이 이 돌의 전부다(사장님 지시 2026-07-30 재설계).
  //   ⚠⚠ **구 「하나를 키운다」(던지고 주우러 간다)는 폐기됐다.** 실플레이에서
  //     *"그냥 개 똥인데? 차라리 근접캐가 나을정도"* — 구조적으로 망한 이유가 명확했다:
  //     **이 게임의 입력은 이동 하나고 그건 이미 「피하는 것」에 다 쓰인다.** 거기에
  //     「주우러 가는 것」을 얹으면 두 목적이 싸우고, 죽는 게 딜 손해보다 나쁘니까
  //     플레이어는 결국 안 줍는다. 안 주우면 그 무기는 아무것도 아니다.
  //     실측(N8·●5): 킬/초 6.38 · 평균 적 420 — 여섯 중 최하위.
  //   ⚠ 「느린 걸 무엇으로 갚나」가 무거움 급의 조건이다. 무거운 돌은 반경으로 갚고,
  //     차돌은 **몸 옆에서 상시로 치는 것**으로 갚는다. 던지는 게 느려도 궤도는 안 쉰다.
  //   축 하나가 셋을 같이 민다(사장님 지시: *"히트박스 커지고, 끈 길이 늘어나고,
  //   도는 속도도 조금씩"*) — 아래 `ORB` 가 어느 지수로 미는지 든다.
  //   값은 `tierMul` 그대로다. **새 절대값이 하나도 없다.**
  chadol: { key: 'orbit',  ko: '도는 반경',   v: byMul(1) },
  // 무거운 돌 — **반경.** 구 `splash: 3.2` × tierMul. 값이 안 바뀐다
  mugeo:  { key: 'splash', ko: '터지는 반경', v: byMul(3.2) },
};
export function stoneAxis(id, t) {
  const a = STONE_AXIS[id];
  if (!a) return { key: null, ko: '', v: 0 };
  return { key: a.key, ko: a.ko, v: a.v[tIdx(t)] };
}

// ── 물수제비 — **간격이 등비로 줄어야 한다** ──────────────────────
// ⚠⚠ **이 규칙이 밸런스의 전부다.** 실제 물수제비가 그렇듯 앞의 대여섯 번이 거리를
//   다 먹고 나머지는 끝에 몰린다. 그래야 ◆32번이 32배 넓어지는 게 아니라
//   **「멀리 가서 끝에 몰아친다」**가 되고, 커버리지가 선형이 아니라 **로그**로 는다.
//
//   총 이동 = gap0 × (1 − decay^n) ÷ (1 − decay)
//     ●○○○○  5튐 →  9.8유닛      ●●●●○ 18튐 → 14.2유닛
//     ●●○○○  8튐 → 12.1유닛      ●●●●● 25튐 → 14.4유닛      ◆32튐 → 14.5유닛
//   → **자국은 6.4배로 느는데 거리는 1.49배**다. 32튐 중 앞 6튐이 거리의 74%를 먹는다.
//
// ⚠⚠ **위 표는 「이론값」이고 화면에서는 그대로 안 나온다.**  【실좌표 실측 2026-07-30】
//   투사체는 프레임 단위로 움직이므로(620px/초 ÷ 60fps = **한 프레임 10.33px**) 등비가
//   10px 아래로 내려가면 **그 밑으로는 못 내려간다.** 실측한 자국 간격 수열(px):
//     93·83·62·52·41·31·31·21·21·21·10·10·10·10·10·10·10·10·10·10·10·10·10·10
//     ↑ 이론 등비(93·74·59·48·38·30·24·19·16·12·10·8·6·5)와 **11번째부터 갈린다.**
//   그래서 예산 25 중 **뒤 14 개가 10px 씩 연속 프레임에 몰려** 140px 안에서 소비된다.
//   실좌표로 다시 잰 값(N8·120초·seed 11·세트 깸, 완주한 발만):
//     ●1  9.03유닛 (앞6몫 100%)      ●4 16.05유닛 (68.8%)
//     ●2 12.04유닛 (92.2%)           ●5 17.75유닛 (**60.7%**)
//     ●3 14.10유닛 (77.8%)
//   → **①은 등급이 오를수록 요구에서 멀어지고(100→60.7%) ②는 1.97배로 1.5배를 넘는다.**
//     즉 아래 두 성질은 **지금 둘 다 안 지켜진다.** 이 파일이 스스로 적어 둔 합격선이
//     사실과 다르므로 그 사실을 여기 남긴다 — 【사장님 판단 대기】.
//     닫는 길은 둘뿐이다: ⓐ 예산 상한을 프레임이 소화할 수 있는 수까지 내린다
//     ⓑ 간격 하한을 「한 프레임 이동」이 아니라 값으로 못 박는다. 둘 다 새 절대값이라
//     여기서 정하지 않는다.
//
// ⚠⚠ **여기 수열이 뜻을 갖는 것은 world 가 「자국을 거리로」 낼 때뿐이다.**
//   【2026-07-30 — 훅·판정 단계에서 실측으로 잡았다】 world 가 처음엔 자국을 **명중**에
//   묶었고, 그러면 자국 수를 예산이 아니라 **명중 횟수**가 정한다. 한 발이 2초에
//   실제로 맞히는 것은 1~2체뿐이라 25 예산 중 **1.6** 만 쓰였다:
//     예산 5 → 발당 1.58 자국 · 예산 25 → 발당 1.66 자국 (**+5%**)
//   즉 위 표와 「로그로 는다」가 통째로 거짓이었다. 고친 뒤:
//     예산 5 → 발당 4.93 · 예산 25 → 발당 23.65 (**4.8배**) · 커버리지 1.50배
//   → **이 표를 고칠 때는 `world.markSkip` 을 같이 읽어라.** 두 곳이 한 규칙이다.
//
// ⚠ `gap0` 은 지어낸 값이 아니라 **물맷돌 사거리에서 역산**했다 — ●●●○○(12튐)의
//   총 이동이 `PASU.range`(13.5유닛)에 정확히 붙게 잡는다:
//     12튐 총합 배수 = (1 − 0.8¹²)/0.2 = 4.6566  →  13.5 ÷ 4.6566 = **2.9유닛**
//   ⚠ 직전 판은 여기서 3.0을 썼는데 그러면 총 이동이 14.0유닛으로 사거리를 넘었다.
//     역산이 낸 값은 2.9다 — 검증에서 잡힌 3.5% 오차를 닫는다.
// ⚠ **gap0·decay 둘 다 【잠정 — 실측 후 확정】**이다. 이 둘이 정하는 것은 값이 아니라
//   **모양**이며, 아래 두 성질이 유지되면 따라 움직여도 된다:
//     ① 앞 6튐이 거리의 3/4를 먹는다   ② t1 → t5 거리가 1.5배 안쪽
//   ⚠⚠ **둘 다 지금은 깨져 있다**(위 실좌표 실측: ① ●5 에서 60.7% · ② 1.97배).
//     이 줄은 「지켜지고 있다」가 아니라 **「지켜야 하는 것」**이다. 초록으로 통과시키지
//     않도록 `gear_world_check` 의 판정문도 실좌표(`m.skipDist`)를 읽게 고쳤다.
// ⚠⚠ **튐마다 광역을 붙이지 마라.** 25개 착탄점에 각각 반경이 붙으면 무거운 돌을
//   통째로 넘는다. (잔돌 12발 × 4히트가 「개사기」였던 그 자리다.)
//   → 그래서 여기에 반경 값이 **없다.** 튐 하나는 그 자리의 한 체를 친다.
// ⚠ **튐 시퀀스는 빨라야 한다.** 새 상수를 두지 않는다 — 투사체가 제 속도로 날면
//   간격이 등비로 줄어드는 만큼 시간도 저절로 줄어든다(총 14.4유닛 ≈ 0.74초).
//   ⚠ **월드 쪽 상수는 `config.SKIPW` 에 있다**(수명 여유·화면 밖 정리). 그쪽은
//     부품이 정하는 값이 아니라 판정이 정하는 값이라 자리가 다르다.
export const SKIP = {
  gap0: 2.9 * UNIT,     // ⚠【잠정】 첫 튐까지의 거리 — 사거리 배율(끈)을 world 가 곱한다
  decay: 0.80,          // ⚠【잠정】 등비. 다음 간격 = 이번 간격 × decay
  // ⚠⚠ **탐색 반경의 하한.**   【사장님 지시 2026-07-30 3차: *"좀만 더 잘 튀게.
  //   지금은 너무 빗나가는게 많아"*】
  //   `markSkip` 이 다음 적을 찾는 반경으로 `s.sgap` 을 쓰는데, 그 값이 등비로 줄어서
  //   **뒤쪽 튐은 적을 아예 못 찾았다** — 0.80^24 = 0.005배(사실상 0). 실측: 직선으로만
  //   뻗은 발이 286 중 130(45%)이었고 그게 「빗나가는」 몫이다.
  //   → 간격(자국이 얼마나 촘촘한가)은 그대로 줄이되 **찾기는 계속 되게** 하한을 둔다.
  //   그래서 뒤로 갈수록 「가까운 놈들 사이를 촘촘히 통통통」이 된다.
  seekMin: 3.6 * UNIT,  // ⚠【잠정】 이 아래로는 탐색 반경이 안 줄어든다
};

// ── 묵은 차돌 — 「도는 돌」  【2026-07-30 재설계】 ─────────────────
// **축 하나(도는 반경)가 셋을 같이 민다** — 사장님 지시:
//   *"등급이 늘어날수록 히트박스 커지고, 끈 길이 늘어나고, 도는 속도도 조금씩."*
//
//   반경  ← 축 그대로        (히트박스 = 끈 길이. 둘은 같은 값이다) — **이 돌의 화력 그 자체**
//   주기  ← 축의 **제곱근**   (`ratePow`) — *"조금씩"*
//
// ⚠⚠ **차돌은 던지지 않는다**(사장님 지시 2026-07-30 2차 재설계).
//   *"던지는게 아니라 그 물멧돌 돌리는 범위 자체가 롤에 가렌처럼 빙글빙글 도는
//   그 범위에 딜이 지속적으로 들어가게 해야지. 그래서 장비 파츠 업그레이드 하면
//   범위가 늘어나도록."*
//   → 투사체가 0 이고(`STONES.melee`), **궤도 상한이 항상 무제한**이다.
//     상한이 남아 있으면 「반경 안에 있으면 계속 맞는다」가 성립하지 않는다 —
//     4마리만 맞고 나머지는 반경 안에 서 있어도 안 맞는다. 그건 지속 피해가 아니다.
//   ⚠ 구 `capPow`(상한을 면적에 비례시키던 지수)는 그래서 **삭제됐다.**
//     상한이 무제한이면 지수가 갈 곳이 없다.
// ⚠ **새 절대값은 세트 배수 둘뿐이다.** 축은 `tierMul` 그대로다.
export const ORB = {
  ratePow: 0.5,   // 주기는 *"조금씩"* — 축의 제곱근
  // ⚠⚠ **축 배정**(사장님 지시 2026-07-30 3차):
  //   반경은 **끈**이 민다. 돌은 **궤적 대역의 두께와 피해**를 민다.
  //   단, 1~4성 차돌은 5성 축에 아직 못 미친 몫만큼 피해와 기본 도달 범위가 덜
  //   전달된다. 5성부터 이 문은 완전히 사라지므로 완성 장비의 끈 축은 그대로다.
  //
  // ⚠⚠ **「광역기가 안 된다」의 원인 셋 — 실측으로 찾았다.**   【2026-07-30 4차】
  //   사장님: *"그냥 근접캐라니까. 광역딜러라고. 휘두르는 모든 부위가 다 딜들어가는거라고.
  //   왜 안되 지금"* → 제대로 낀 시작 상태(차돌●1 + 묵은●1)를 재보니:
  //       반경 **58px**   — 몸(반지름 12.8)에서 45px. 화면 1920×1080 에서 안 보인다
  //       틱 **0.717s**  — 0.7초에 한 번 「똑」. 지속 피해로 안 느껴진다
  //       초당 **6.9**   — 던지는 고른 돌 ●1 이 초당 17. **던지기를 버렸는데 40%** 다
  //   ⓐ `ORBIT.dmgRatio 0.55` 가 궤도를 절반으로 깎았다. 그 근거는 *"던지는 것보다
  //      세면 던질 이유가 없다"* 였는데 — **차돌은 던지지 않으므로 근거 자체가 없다.**
  //      → `dmgFull` 로 우회한다. 이게 제일 크다(즉시 ×1.82).
  //   ⓑ 끈 등급 기여가 ●○○○○ 에서 `tierMul(1)^1.5 = 0.465` 로 **반경을 반토막** 냈다.
  //      → `cordFloor` 로 하한을 둔다. 등급이 낮아도 「내 주변」이 보여야 한다.
  //   ⓒ 틱이 느렸다. → `tick` 으로 차돌만 촘촘하게 돈다.
  meleeR:   3.0,   // ⚠【잠정】 차돌 기본 반경 배수 — 「광역기 수준」의 밑
  cordPow:  1.5,   // ⚠【잠정】 끈 등급 기여 지수
  cordFloor: 0.8,  // ⚠【잠정】 그 기여의 **하한** — ●○○○○ 도 광역으로 보여야 한다
  tick:     0.18,  // ⚠【잠정】 차돌 전용 궤도 주기(초). `ORBIT.tick 0.40` 을 대신한다
  dmgFull:  true,  // **던지지 않으므로 `dmgRatio` 를 안 탄다** — 위 ⓐ
  setR:    [1.25, 1.5],  // ⚠【잠정】 묵은 세트 — 도는 원이 커진다(작게 · 크게)
  bandBase: 0.35,  // ⚠【잠정】 궤적 대역 두께(반경 대비). 돌 등급이 민다
};

// 1~4성 단단한 돌의 미완성 전달 정본. `null`은 5성·◆가 이 산술을 전혀 타지
// 않는다는 뜻이다. 피해·범위가 서로 다른 파일에서 같은 식을 다시 쓰지 않게 한다.
export function chadolEarlyTransmission(tier) {
  const t = Math.max(1, tier | 0);
  if (t >= TIER_MAX) return null;
  const damage = stoneAxis('chadol', t).v / stoneAxis('chadol', TIER_MAX).v;
  const gradualRange = Math.pow(damage, ORB.ratePow);
  if (t !== TIER_MAX - 1) return { damage, range: gradualRange };

  // 4성은 작은 묵은 세트가 처음 열리며 후반 무한 타격 면적이 급증한다. 새 배수를
  // 만들지 않고 같은 미완성 축을 반경에 한 번 더 전달한다(√damage × √damage).
  // 다만 그 값은 3성의 점진 전달보다 0.3% 작으므로, 바로 전 등급의 기존 전달률을
  // 바닥으로 삼아 어떤 부품 조합에서도 돌 3→4성 반경이 역행하지 않게 한다.
  const previousDamage = stoneAxis('chadol', t - 1).v / stoneAxis('chadol', TIER_MAX).v;
  const monotoneFloor = Math.pow(previousDamage, ORB.ratePow);
  return { damage, range: Math.max(damage, monotoneFloor) };
}

// ══════════════════════════════════════════════════════════════════
//  받침 6종 — 무는 개수 × 물림 정도
//
//  ⚠⚠ **받침의 축은 「개수 ↔ 발당 위력」이다.** 처음엔 「개수 + 확산」 둘로 짰는데
//    **그 둘이 사실 한 값이었다** — `world` 발사부가 `k === 1 ? 0 : …` 이라 **1발이면
//    확산이 아무 일도 안 한다.** 정확 계열(hold 1)은 확산을 0.7로 줄이든 1.6으로
//    늘리든 화면이 똑같았고, 명중(hitPad)도 조준이 최근접 1체 고정(불가침 3)이라
//    1발은 이미 100% 명중이다 — 두꺼운 가죽 ●●→●●●●● 의 pad 차이가 **0.2px**였다.
//    → **꽉 물면 힘이 그대로 실리고 헐거우면 새어 나간다.** 물맷돌이 실제로 그렇고,
//      그러면 축이 하나로 선다: **적게 물고 세게 ↔ 많이 물고 약하게.**
//
//  ■ 6종으로 늘었다(무거움 +1). **새 발명이 아니라 기존 다섯의 이름만 바꿔 자리에
//    넣고, 무거움 급에 하나만 새로 만들었다.** 배정 기준은 **「그 돌의 성질을
//    받쳐주는가」**다:
//      마른(잔돌 small)  → holdSmall 6. 잔돌은 「수」이므로 제일 많이 무는 것이 짝
//      성긴(고른 돌 선)  → hold 1 · 꽉. 선은 한 발이라야 선이다(확산이 실질 0)
//      거친(납작한 돌)   → hold 1 · 꽉. 투사체가 오래 살아 겹치면 화면이 잠긴다
//      길든(모난 돌 방사)→ hold 2/4. 파편은 발마다 나므로 다발이 짝
//      묵은(차돌 시간)   → **hold 2** (아래 C-1 예외)
//      굳은(무거운 돌)   → hold 1 · 제일 꽉
//
//  ⚠ **어긋난 자리 — 이름과 값이 반대로 읽힌다(보고 대상).**
//    「마른 받침」이 실물은 **그물**(6개를 문다)이고, 「성긴 받침」이 실물은
//    **얇은 가죽**(하나를 꽉 문다)이다. 사장님 지시대로 **값을 우선**했다 —
//    *"네이밍은 나중에 바꾸면 되는거잖아."*
//
//  ── C-1. **묵은 받침만 예외 — hold 2** ────────────────────────────
//  차돌 개수가 받침을 따르는데 **무거움 급 받침은 전부 `hold: 1`** 이라 그대로면
//  차돌이 항상 하나다. 이 파일이 *"무거움 급에 다발 계열은 두지 않는다 — 무거운
//  것을 여러 개 담으면 안 나간다"* 고 못 박아 뒀는데,
//  **차돌은 회수해서 다시 쓰는 돌이라 매 발사가 새 돌이 아니다** — 원칙의 전제가
//  다르다. 담는 것이 아니라 **되돌아온 것을 다시 무는 것**이다.
//  → 값은 두꺼운 가죽에서 파생했다. 개수를 하나 더 무는 대가를 **보통 급의 교환비
//    (오므린→넓은: hold+1 에 dmg −0.20 · spread +0.55 · aim −0.20)의 절반**으로
//    치렀다. 무거움은 애초에 적게 무는 급이라 대가도 절반이다.
//      dmg   1.20 − 0.10 = 1.10    spread 0.80 + 0.275 = **1.075**
//      aim   1.05 − 0.10 = 0.95    holdSmall 2 + 1 = 3
//    ⚠ 직전 판은 spread 를 1.05 로 적었는데 그것은 위 규칙이 내는 값이 아니다(1.075).
//      규칙을 적어 놓고 규칙 밖의 값을 쓰면 그 규칙이 거짓말이 된다 — 값을 맞췄다.
//
//  ── ⚠⚠ 예외를 **차돌에만** 건다 — `holdKeep`   【검증 2026-07-30】 ─────
//  처음에는 `hold: 2` 로 잡았는데, 그러면 **무거운 돌에도 그대로 걸린다**(둘 다 무거움
//  급이라 묵은 받침에 물린다). 실측: 무거운 돌 ●●●●● 이 발 3이 되고 **착탄 광역이
//  발마다** 붙어 이 파일이 스스로 금지한 *"기본 광역이 발 수에 곱해지는"* 자리가 열렸다
//  (N8 평균 적 130 → 109). C-1 의 근거는 *"차돌은 회수해서 다시 쓰는 돌이라 매 발사가
//  새 돌이 아니다 — 담는 것이 아니라 **되돌아온 것을 다시 무는 것**"* 이었고,
//  그 문장은 무거운 돌에 성립하지 않는다. **근거가 차돌에만 있으므로 예외도 차돌에만 건다.**
//  → `hold: 1` (무거운 돌은 구 값 그대로) + `holdKeep: 2` (되돌아오는 돌만 둘을 문다).
//    새 개념이 아니다 — C-1 이 적어 둔 근거를 그대로 값으로 옮긴 것뿐이다.
//  → 굳은 받침(무거운 돌)은 `hold: 1` 유지.
// ══════════════════════════════════════════════════════════════════
export const CRADLES = [
  // 【기존 그물 받침】
  { id: 'dry',   setId: 'dry',   cls: CLS.light, hold: 1, holdSmall: 6, spread: 1.60, aim: 0.80, dmg: 0.90 },
  // 【기존 얇은 가죽】
  { id: 'loose', setId: 'loose', cls: CLS.light, hold: 1, holdSmall: 1, spread: 0.70, aim: 1.15, dmg: 1.10 },
  // 【기존 오므린 가죽】
  { id: 'rough', setId: 'rough', cls: CLS.mid,   hold: 1, holdSmall: 2, spread: 0.75, aim: 1.10, dmg: 1.15 },
  // 【기존 넓은 가죽】
  { id: 'tamed', setId: 'tamed', cls: CLS.mid,   hold: 2, holdSmall: 4, spread: 1.30, aim: 0.90, dmg: 0.95 },
  // 【신규 — C-1 예외. 두꺼운 가죽 + 「하나 더 문다」의 대가 절반】
  // ⚠ 구 `holdKeep: 2`(차돌만 둘을 문다)는 **삭제됐다** — 차돌이 근접(궤도)이 되면서
  //   「필드에 남는 돌 개수」라는 개념 자체가 사라졌다. 무거움 급은 다시 hold 1 로 통일이고,
  //   묵은/굳은은 **확산과 위력의 반대편**으로 갈린다(묵은 넓고 약함 · 굳은 좁고 셈).
  { id: 'aged',  setId: 'aged',  cls: CLS.heavy, hold: 1, holdSmall: 3, spread: 1.075, aim: 0.95, dmg: 1.10 },
  // 【기존 두꺼운 가죽】
  { id: 'hard',  setId: 'hard',  cls: CLS.heavy, hold: 1, holdSmall: 2, spread: 0.80, aim: 1.05, dmg: 1.20 },
];

// ══════════════════════════════════════════════════════════════════
//  끈 6종 — **굵을수록 짧다**
//  무거운 것을 길게 돌리려면 힘이 훨씬 드니 물리적으로 맞고, 두 축(사거리·급)이
//  한 축으로 합쳐진다. **사거리 = 흡수 반경**(10-B 2-3)이라 **끈이 곧 수입이다.**
//
//  ■ 3 → 6. **급마다 둘**이고, 둘의 차이는 「멀리·흩어지게 ↔ 덜 멀리·곧게」다.
//    ⚠ 두 값이 **같은 방향으로** 움직인다(현재 표가 그렇다: 1.20/1.25 → 1.00/1.00 →
//      0.80/0.85). 사거리만 올리고 확산만 내리는 부품은 **덫 없는 순이득**이 되므로
//      만들지 않는다. 기존 셋이 「멀리」 쪽, 신규 셋이 「곧게」 쪽이다.
//
//  ■ 배정 근거
//    성긴(고른 돌) 이 제일 길다 — **선이 화면을 가르려면 멀리 가야** 하고, 성긴
//      받침이 hold 1 이라 **확산 1.25 는 실질 0**이다(1발엔 확산이 안 걸린다)
//    마른(잔돌) 은 조금 짧고 곧다 — 12발이 **다 맞아야** 「수」가 의미를 갖는다
//    굳은(무거운 돌) 이 무거움 급에서 더 길다 — *"**멀리** 한 자리가 크게 터진다"*
//    묵은(차돌) 이 제일 짧다 — **되찾아야 하는 돌**이라 멀리 보내면 안 된다.
//      회수 반경은 `PLAYER.pickup`(9유닛)으로 고정이므로 던지는 거리가 곧 왕복 비용이다
//
//  ⚠ 거친 1.00/1.00 · 굳은 0.80/0.85는 구 파일에서 왔고, 가벼움 급 사거리는
//    실플레이의 긴 사거리를 줄인 현재값(1.15/1.20)이다. 나머지 신규 값은
//    그 사이·바깥을 같은 눈금으로 채운 【잠정 — 측정 대기】 값이다.
//    도출 규칙은 하나뿐이다: **같은 급 안에서 「멀리·흩어지게」의 반대쪽을 만든다.**
//    크기 자체는 아직 아무것도 재지 않았다.
// ══════════════════════════════════════════════════════════════════
export const CORDS = [
  { id: 'dry',   setId: 'dry',   cls: CLS.light, range: 1.15, spread: 1.05 },  // ⚠【잠정】 신규(곧게)
  { id: 'loose', setId: 'loose', cls: CLS.light, range: 1.20, spread: 1.25 },  // 기존 가는 끈
  { id: 'rough', setId: 'rough', cls: CLS.mid,   range: 1.00, spread: 1.00 },  // 기존 땋은 끈
  { id: 'tamed', setId: 'tamed', cls: CLS.mid,   range: 0.90, spread: 0.80 },  // ⚠【잠정】 신규(곧게)
  // ⚠⚠ **뒤집었다.**   【차돌 재설계 2026-07-30】
  //   구 값은 `range 0.72`(여섯 중 제일 짧다)였고 근거가 *"차돌은 되찾아야 하는 돌이라
  //   멀리 보내면 왕복 비용이다"* 였다. 회수가 사라졌으므로 **그 근거가 통째로 없어졌다.**
  //   지금 차돌의 정체는 근접이고 **끈이 곧 궤도 반경 = 히트박스**다
  //   (사장님 지시: *"등급이 늘어날수록 히트박스 커지고, **끈 길이 늘어나고**"*).
  //   → 무거움 급 안에서 **긴 쪽**으로 옮긴다. 급의 원칙(무거우면 짧다)은 지킨다 —
  //     둘 다 기준(1.00) 밑이고, 묵은/굳은이 **축의 반대편**으로 갈린다:
  //       묵은 = 멀리·흩어짐(궤도가 크다)   ·   굳은 = 가깝게·곧게
  { id: 'aged',  setId: 'aged',  cls: CLS.heavy, range: 0.88, spread: 0.92 },  // ⚠【잠정】
  { id: 'hard',  setId: 'hard',  cls: CLS.heavy, range: 0.80, spread: 0.85 },  // 기존 힘줄 끈
];

// ══════════════════════════════════════════════════════════════════
//  걸이 6종 — **연사 ↔ 궤도**   【D. 축 이동: 명중 → 궤도】
//
//  ⚠ `aim` 은 **거의 죽은 값이었다.** 조준이 최근접 1체 고정(불가침 3)이라 1발이면
//    이미 100% 명중이고, 실측 pad 차이가 **0.2px** 였다(이 파일이 스스로 기록했다).
//    → **걸이의 `aim` 을 `orbit`(궤도)으로 바꾼다.** 값을 그대로 옮겼다:
//        뼈 고리 aim 0.80 → 마른 고리 orbit 0.80
//        가죽 고리 1.00   → 길든 고리 1.00
//        두 겹 고리 1.20  → 굳은 고리 1.20
//      새 부품 셋은 그 사이/바깥을 **같은 0.1~0.2 눈금**으로 채운다.
//  ⚠ **받침의 `aim` 은 그대로 둔다.** 없앤 것은 걸이의 것뿐이다.
//  ⚠ 그 결과 **확산을 만드는 부위가 셋에서 둘(받침·끈)로 준다** — 구 `spreadMul` 이
//    걸이 명중으로 `(2 − aim)` 을 곱하고 있었다. 그 항이 사라진다(resolveGear 참조).
//
//  ■ **묵은 걸이가 궤도를 제일 강하게 민다** — 차돌은 던지면 필드에 남으므로 회수
//    왕복 동안 손이 빈다. **그 공백을 궤도가 메운다.** 그래서 연사는 제일 낮고
//    궤도는 제일 높다. 묵은 끈이 제일 짧은 것과 한 벌이다 — *반경은 작고 피해는
//    크다 = 몸에 딱 붙어서 세게 돈다.* 차돌 주우러 몸으로 돌아다니는 동안 몸 옆이 세다.
//
//  ⚠⚠ **신규 셋(성긴·거친·묵은)의 여섯 값은 전부 【잠정 — 측정 대기】다.**
//    기존 셋의 `aim` 을 `orbit` 자리로 **그대로** 옮긴 것(0.80 / 1.00 / 1.20)만 구 값이고,
//    나머지 여섯은 그 사이·바깥을 0.05~0.20 눈금으로 채운 것이다.
//    ⚠ 특히 **묵은 고리는 구 범위 밖**이다 — rate 0.80 은 구 최저(0.85)보다 낮고
//      orbit 1.40 은 구 최고(1.20)를 넘는다. *"차돌은 회수 공백을 궤도가 메운다"* 는
//      설계 D 의 요구를 값으로 옮긴 것이라 밖으로 나가는 것 자체가 의도이지만,
//      **얼마나 밖인지는 아직 아무것도 재지 않았다.**
// ══════════════════════════════════════════════════════════════════
export const LOOPS = [
  { id: 'dry',   setId: 'dry',   cls: CLS.light, rate: 1.25, orbit: 0.80 },  // 기존 뼈 고리
  { id: 'loose', setId: 'loose', cls: CLS.light, rate: 1.15, orbit: 0.90 },  // ⚠【잠정】 신규
  { id: 'rough', setId: 'rough', cls: CLS.mid,   rate: 1.05, orbit: 0.95 },  // ⚠【잠정】 신규
  { id: 'tamed', setId: 'tamed', cls: CLS.mid,   rate: 1.00, orbit: 1.00 },  // 기존 가죽 고리
  { id: 'aged',  setId: 'aged',  cls: CLS.heavy, rate: 0.80, orbit: 1.40 },  // ⚠【잠정】 신규 — 궤도 최강
  { id: 'hard',  setId: 'hard',  cls: CLS.heavy, rate: 0.85, orbit: 1.20 },  // 기존 두 겹 고리
];

// ── 조립 ──────────────────────────────────────────────────────────
export const SLOTS = ['stone', 'cradle', 'cord', 'loop'];
export const SLOT_KO = { stone: '돌', cradle: '받침', cord: '끈', loop: '걸이' };
export const PARTS = { stone: STONES, cradle: CRADLES, cord: CORDS, loop: LOOPS };
export const PART_BY_ID = {};
// **이름을 여기서 한 번에 붙인다** — 정의부에는 문자열이 없다(위 「이름」 절 참조).
//   ⚠ 돌 이름에도 세트 수식어가 들어 있다. `STONE_KO` 가 확정 이름의 정본이다.
for (const s of SLOTS) for (const p of PARTS[s]) {
  p.ko = s === 'stone'
    ? STONE_KO[p.id]
    : `${SET_KO[p.setId]} ${PART_SUFFIX[s]}`;
  PART_BY_ID[`${s}:${p.id}`] = p;
}

// ══════════════════════════════════════════════════════════════════
//  세트 — 수식어 6 × 부위 4 = 24. 각 칸에 정확히 하나   【E】
//
//  **판정: 4칸 `setId` 가 전부 같다 + 네 칸의 최저 등급이 4 이상이다.**
//  최저 등급이 ●4면 「작게」다. 따라서 4·4·5·◆처럼 4와 상위 등급이 섞여도
//  어렵게 모은 수식어 네 칸을 끄지 않는다. 네 칸이 전부 ●5 이상이면 「크게」다.
//  ●5에서 ◆로 한 칸씩 올리는 과정도 이미 완성한 큰 세트를 끄지 않는다
//  (5·5·◆·5도 「크게」 유지).
//  네 칸이 **전부 ◆**일 때만 「◆」단계다. 하나라도 5가 남으면 기존 「크게」다.
//  ⚠ **표시는 색으로 하지 마라** — `TIER_COLOR` 5색이 이미 등급을 말한다.
//    **궤도에 이펙트**를 얹는다(테두리·글로우 같은 다른 채널). 그림은 scene 소관.
// ══════════════════════════════════════════════════════════════════
export function setOf(gear) {
  const ids = SLOTS.map(s => gear?.[s]?.p?.setId);
  if (ids.some(x => !x)) return null;
  if (new Set(ids).size !== 1) return null;              // 4칸이 같은 수식어라야 한다
  const ts = SLOTS.map(s => gear[s].t ?? 1);
  const t = Math.min(...ts);                             // 세트를 지탱하는 최저 등급
  if (t < 4) return null;
  const large = t >= TIER_MAX;                           // ●5/◆ 혼합은 큰 세트 유지
  const diamond = ts.every(t => t >= TIER_DIAMOND);      // ◆·◆·◆·◆에서만 전용 단계
  return { id: ids[0], ko: SET_KO[ids[0]], fx: SET_FX_KO[ids[0]],
           diamondFx: SET_DIAMOND_FX_KO[ids[0]], t,
           level: diamond ? 'diamond' : large ? 'large' : 'small' };
}

/** 고른 여섯 조합 중 정확한 2+2만 판정한다. 순서와 슬롯 위치에는 의존하지 않는다. */
export function hybridSetOf(gear) {
  const byFamily = new Map();
  for (const slot of SLOTS) {
    const part = gear?.[slot];
    const id = part?.p?.setId;
    if (!id) return null;
    const xs = byFamily.get(id) ?? [];
    xs.push(Math.max(1, Math.min(TIER_DIAMOND, part.t | 0 || 1)));
    byFamily.set(id, xs);
  }
  if (byFamily.size !== 2 || [...byFamily.values()].some(xs => xs.length !== 2)) return null;
  const families = [...byFamily.keys()].sort();
  const def = HYBRID_SETS.find(x => [...x.families].sort().every((id, i) => id === families[i]));
  if (!def) return null;
  const familyTiers = Object.fromEntries(def.families.map(id => [id, Math.min(...byFamily.get(id))]));
  const t = Math.min(...Object.values(familyTiers));
  const stage = t >= 5 ? 3 : t >= 3 ? 2 : 1;
  return Object.freeze({
    id: def.id, ko: def.ko, fx: def.fx, hook: def.hook,
    families: def.families, familyTiers: Object.freeze(familyTiers),
    t, stage, level: stage === 3 ? 'large' : stage === 2 ? 'tempered' : 'small',
  });
}
// 화면용 — 「같은 수식어가 몇 칸인가」(F. 막대 UI 의 세트 표시). 발동 여부와 별개다.
export function setCount(gear) {
  const n = {};
  for (const s of SLOTS) { const id = gear?.[s]?.p?.setId; if (id) n[id] = (n[id] ?? 0) + 1; }
  let best = null, k = 0;
  for (const [id, c] of Object.entries(n)) if (c > k) { best = id; k = c; }
  return { id: best, ko: best ? SET_KO[best] : '', n: k };
}

// ── 급 궁합 — **얼마나 단단히 무나** ───────────────────────────────
// 모든 조합을 낀다. 돌과 부품의 1·2·3급 자체에서만 물림 비율을 낸다:
//   2급 돌←1급 부품 = 1/2 · 3급←2급 = 2/3 · 3급←1급 = 1/3.
// 같은 급과 상위 급은 1이라 기존 조합의 연산을 한 비트도 바꾸지 않는다.
export function classGrip(stone, part) {
  if (!stone || !part) return 1;
  return Math.min(1, (part.cls + 1) / (stone.cls + 1));
}
export function canHold(stone, part) {
  return true;
}
export function fits(gear, stone) {
  return true;
}
// 구 호출부 호환. 이제 막는 부위는 없고, UI가 물림 손실을 말할 때는 looseSlots를 쓴다.
export function blockers(gear, stone) {
  return [];
}
export function looseSlots(gear, stone = gear?.stone?.p) {
  if (!stone) return [];
  return ['cradle', 'cord', 'loop'].filter((slot) => classGrip(stone, gear?.[slot]?.p) < 1);
}

// ── 돌 피해 전달 — **별 등급 전달과 급 물림은 서로 대신하지 못한다** ──────────
// 높은 등급의 낮은 급 부품이 `tierMul × classGrip` 한 값으로 급 차이를 지워 버리면,
// 3급 돌 + 1급 ◆ 세 부위가 다시 물림 100%가 된다. 등급과 급은 서로 다른 질문이므로
// 각각 전달률을 낸 뒤 곱한다:
//   tierTx  = 바닥 + (1 − 바닥) × min(1, 부품 평균 등급 배수 / 돌 등급 배수)
//   classTx = 바닥 + (1 − 바닥) × 부품 평균 classGrip
//   support = tierTx × classTx
// 같은 급·같은 등급은 둘 다 1이라 기존 결과가 정확히 유지된다. 낮은 급이면 부품
// 등급이 아무리 높아도 classTx < 1이라 물림 100%가 될 수 없다.
export const SUPPORT_FLOOR = 0.30;
export function supportBreakdownOf(gear) {
  const st = gear.stone;
  if (!st) return { tierTx: 1, classTx: 1, value: 1 };
  const slots = ['cradle', 'cord', 'loop'];
  // ⚠ **등급 「숫자」가 아니라 「배수」로 잰다.** 배수가 이미 비선형(0.6~2.2)인데
  //   숫자 비(1~6)는 선형이라 섞으면 위 균형점 계산이 안 맞는다.
  const tierAvg = slots.reduce((sum, slot) => sum + tierMul(gear[slot]?.t ?? 1), 0)
    / slots.length;
  const classAvg = slots.reduce((sum, slot) =>
    sum + classGrip(st.p, gear[slot]?.p), 0) / slots.length;
  const tierR = Math.min(1, tierAvg / tierMul(st.t));
  const tierTx = SUPPORT_FLOOR + (1 - SUPPORT_FLOOR) * tierR;
  const classTx = SUPPORT_FLOOR + (1 - SUPPORT_FLOOR) * classAvg;
  return { tierTx, classTx, value: tierTx * classTx };
}
export function supportOf(gear) {
  return supportBreakdownOf(gear).value;
}

// ── 기본 광역 바닥 — **모든 돌이 최소한 이만큼은 한다** ────────────
// 물맷돌이 1:1이면 물량을 구조적으로 못 이긴다(무강화 1.5킬/초 vs 스폰 12.6마리/초).
//   · 관통 — 한 발이 뚫는 체수다. 발이 많으면 총 관통이 저절로 는다 → **나눈다**
//   · 파편 — **제곱근 감쇠.** 발이 많으면 총 파편이 늘되 선형이 아니다.
//            1발 2.0 · 4발 총 4.0 · 12발 총 6.9 — 다발에 보상은 주고 폭주는 막는다.
export const BASE_PIERCE = 1;
export const BASE_SHARDS = 2;
// 가벼운 돌의 관통은 세트 보너스가 아니라 **돌 자체의 +1**이다.
// `null`은 고유 보너스가 없다는 뜻이고, 1은 공통 바닥 `BASE_PIERCE / shots`를
// 대체하지 않고 그 위에 더한다. 투사체가 없는 돌과 무한 관통 돌에는 표시도
// 판정도 중복시키지 않는다.
export function stonePierceValue(stone) {
  return stone?.setId === 'dry' && !stone?.melee && !stone?.pierceInf ? 1 : null;
}
// ⚠ **상·하한은 폭주 방지 난간이지 규칙이 아니다.** 확산을 만드는 부위가 셋에서
//   둘(받침·끈)로 줄어(D) 24×24 전 조합 실측 범위가 **0.450 ~ 2.000** 이 됐다 —
//   `SPREAD_MIN` 은 걸리고 `SPREAD_MAX` 는 **한 번도 안 걸린다**(구 구조에서도 최대
//   2.400 이라 이미 안 걸렸다). 그래도 남긴다: 난간은 닿지 않을 때가 정상이고,
//   부품이 하나 더 붙는 날 이 값이 없으면 조용히 폭주한다. **닿지 않는다는 사실을
//   여기 적어 두는 것**이 규율 5 의 「왜 남기는지 적어라」다.
export const SPREAD_MIN = 0.45;
export const SPREAD_MAX = 2.60;
// ── 「연사 상한을 푼다」의 유한 표현 ──────────────────────────────
// ⚠ **밸런스 값이 아니다.** 성긴 세트 「크게」에서 발 간격이 0 이 되는데 그 뜻은
//   「상한이 없다」이고, 그것을 `Infinity` 로 적으면 `JSON.stringify` 한 번에 `null` 이
//   되어 **연사가 0 이 된다**(자세한 근거는 `resolveGear` 의 `rateCapMul` 주석).
//   하드 상한(4발/초)의 1000배 = 4000발/초는 전 조합 실측 최대(4.83발/초)의 800배라
//   어떤 빌드도 못 닿는다 — **수가 아니라 문턱**이다.
export const CAP_OPEN = 1000;

// ── 부가 줄 — 부위마다 서로 다른 행동 어휘를 쓴다 ───────────────────
// 피해 배수 네 줄을 겹치는 대신 돌=명중, 받침=대형, 끈=궤적, 걸이=박자라는
// 물리 역할을 그대로 행동으로 보인다. 값은 모두 유한 카운터/1회 훅이며 난수를 쓰지 않는다.
const trait = (slot, id, ko, summary, hook, v) => Object.freeze({ slot, id, ko, summary, hook, v });
export const SLOT_TRAITS = Object.freeze({
  stone: Object.freeze([
    trait('stone', 'stone_split', '갈라지는 면', '첫 명중이 작은 조각 하나를 낸다', 'hitShard', 1),
    trait('stone', 'stone_echo', '되울림', '네 번째 명중이 한 번 되울린다', 'hitEcho', Object.freeze({ every: 4, ratio: 0.35 })),
    trait('stone', 'stone_mark', '금 간 자리', '맞은 자리를 잠시 금가게 한다', 'hitMark', Object.freeze({ bonus: 0.24, sec: 2.4 })),
    trait('stone', 'stone_shove', '밀리는 결', '명중한 땅의 적을 조금 밀어낸다', 'hitPush', 18),
  ]),
  cradle: Object.freeze([
    trait('cradle', 'cradle_focus', '모아 물기', '부채를 모으고 궤도 안쪽을 채운다', 'formationFocus', 0.72),
    trait('cradle', 'cradle_flank', '가장자리 물기', '양끝 발이 바깥 먹잇감을 한 번 찾는다', 'formationFlank', 1),
    trait('cradle', 'cradle_cross', '엇갈려 물기', '발사 묶음이 좌우로 번갈아 어긋난다', 'formationCross', 1),
    trait('cradle', 'cradle_stagger', '층층이 물기', '발마다 속도와 궤도 층이 갈린다', 'formationStagger', 0.16),
  ]),
  cord: Object.freeze([
    trait('cord', 'cord_return', '되감는 끈', '빗나간 돌이 한 번 되돌아온다', 'returnShot', 0.68),
    trait('cord', 'cord_seek', '휘어드는 끈', '첫 명중 뒤 다른 먹잇감으로 한 번 꺾인다', 'seekAfterHit', 1),
    trait('cord', 'cord_curve', '굽은 끈', '비행 궤적이 시간에 따라 일정하게 휜다', 'curveShot', 0.55),
    trait('cord', 'cord_linger', '긴 여운', '비행과 궤도 여운이 조금 더 남는다', 'lingerShot', 0.45),
  ]),
  loop: Object.freeze([
    trait('loop', 'loop_breath', '들이쉬고 내쉬기', '짧고 긴 발사 간격이 번갈아 뛴다', 'rhythmSwing', 0.28),
    trait('loop', 'loop_burst', '네 박자 매듭', '네 번째 박자가 작은 메아리를 한 번 낸다', 'rhythmBurst', Object.freeze({ every: 4, ratio: 0.42 })),
    trait('loop', 'loop_hold', '놓치지 않는 매듭', '빈 박자를 저장해 다음 돌을 되감는다', 'rhythmHold', 1),
    trait('loop', 'loop_third', '셋째 매듭', '세 번째 발사가 한 번 더 먹잇감을 찾는다', 'rhythmThird', 3),
  ]),
});
export const TRAITS = Object.freeze(SLOTS.flatMap(slot => SLOT_TRAITS[slot]));
export const TRAIT_IDS = Object.freeze(TRAITS.map(t => t.id));

// 구 세이브에 이미 적힌 id는 읽되 새 부품에는 다시 배정하지 않는다.
export const LEGACY_TRAITS = Object.freeze([
  { id: 'first', ko: '첫 발이 세다', hook: 'firstShot', v: 0.45 },
  { id: 'chain', ko: '연달아 맞으면 는다', hook: 'rampHit', v: 0.08 },
  { id: 'big', ko: '큰 놈에게 더 아프다', hook: 'eliteDmg', v: 0.30 },
  { id: 'far', ko: '멀리서 더 아프다', hook: 'farDmg', v: 0.25 },
  { id: 'roll', ko: '빗나가면 굴러간다', hook: 'missRoll', v: 1 },
  { id: 'bounce', ko: '맞고 튄다', hook: 'ricochet', v: 1 },
  { id: 'idle', ko: '손이 안 쉰다', hook: 'idleRate', v: 0.30 },
  { id: 'yield', ko: '더 남긴다', hook: 'pieceBonus', v: 0.20 },
]);
export const TRAIT_BY_ID = Object.freeze(Object.fromEntries([...TRAITS, ...LEGACY_TRAITS].map(t => [t.id, t])));

function traitHash(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193) >>> 0;
  return h >>> 0;
}

/** 같은 카탈로그 키는 언제나 같은 줄을 얻는다. 호출 때마다 새 배열을 반환한다. */
export function deterministicTraits(slot, partId, tier) {
  const pool = SLOT_TRAITS[slot];
  const count = traitCount(tier);
  if (!pool || count === 0) return [];
  const h = traitHash(`${slot}:${partId}`);
  const start = h % pool.length;
  const step = (h & 0x100) ? 1 : pool.length - 1;
  return Array.from({ length: count }, (_, i) => pool[(start + i * step) % pool.length].id);
}

export function traitDetails(part) {
  return (part?.traits ?? []).map(id => TRAIT_BY_ID[id]).filter(Boolean);
}

// ══════════════════════════════════════════════════════════════════
//  해석 — 슬롯 4칸 → 배율 다발
//  **네 칸이 전부 곱이라 한 칸을 올리면 나머지가 같이 산다.**
//  gear = { stone:{p,t,traits[]}, cradle:{...}, cord:{...}, loop:{...} }
// ══════════════════════════════════════════════════════════════════
export function resolveGear(gear) {
  // ⚠ 슬롯은 **래퍼**다 — { p: 부품정의, t: 등급, traits: [] }.
  const S = gear.stone?.p, C = gear.cradle?.p, R = gear.cord?.p, L = gear.loop?.p;
  const stT = gear.stone?.t ?? 1, crT = gear.cradle?.t ?? 1;
  const cordT = gear.cord?.t ?? 1, loopT = gear.loop?.t ?? 1;
  const sm = tierMul(stT), cm = tierMul(crT);
  const rm = tierMul(cordT), lm = tierMul(loopT);
  const cradleGrip = classGrip(S, C);
  const cordGrip = classGrip(S, R);
  const loopGrip = classGrip(S, L);
  const support = supportBreakdownOf(gear);
  const sup = support.value;

  // ── 【사장님 결정 2026-07-29】 **등급은 순이득이다** ──────────────
  //   *"등급은 레벨에 비례해서 더 좋아지게. 초반에 4~5 성급 뜨면 쉬워져야 되."*
  // → **장점 축만 등급을 탄다.** 단점 축(1 미만)은 **부품 고유값 그대로 고정**이다.
  const dev = (v, m) => { const x = v ?? 1; return x >= 1 ? Math.pow(x, m) : x; };
  // 낮은 급 부품도 끼되 그 부품의 **좋은 몫만** 중립값 1 쪽으로 감쇠한다.
  // 이미 등급을 탄 효과를 classGrip 거듭제곱으로 되돌리는 것과 같은 식이다:
  //   (v^m)^grip = v^(m×grip). 불리한 축까지 완화하면 낮은 급이 오히려 보상이 되므로
  //   높은 쪽이 좋은 값은 v<1, 낮은 쪽이 좋은 값은 v>1을 기존 그대로 둔다.
  const devGrip = (v, m, grip) => {
    const x = v ?? 1;
    return x >= 1 ? Math.pow(x, m * grip) : x;
  };
  const devLowGrip = (v, m, grip) => {
    const x = v ?? 1;
    return x <= 1 ? Math.pow(x, m * grip) : x;
  };

  // 낮은 급의 빠른 걸이는 좋은 편차만 중립으로 되돌려도 무거운 걸이보다 빨라서,
  // 물림 손실을 상쇄하고도 주력 위력/초가 완전 물림보다 높아졌다. 던지는 돌에서만
  // 걸이의 **전체 박자 전달**에 해당 슬롯의 급 전달률을 한 번 건다. 차돌은 걸이의
  // 연사↔궤도 교환비 자체가 주력 정체성이고 이미 완전 물림보다 낮아 이 갈래를 타지 않는다.
  const loopClassTx = SUPPORT_FLOOR + (1 - SUPPORT_FLOOR) * loopGrip;
  const loopRateMul = devGrip(L?.rate, lm, loopGrip)
    * (!S?.melee && loopGrip < 1 ? loopClassTx : 1);

  // 같은 돌의 낮은 등급이 한 발 위력에서 상위 등급을 앞지르지 못하게 한다.
  // 등급 전달만 현재 돌 등급까지의 최댓값으로 단조화한 뒤, 급 전달은 바깥에서 딱 한 번
  // 곱한다. classTx를 max 안에 넣으면 낮은 돌 등급에서 tier 여유가 급 손실까지 지워
  // 모난 돌의 헐거운 물림이 다시 완전 피해가 되는 구멍이 열린다.
  let supportedStoneDmg = 0, supportedOrbitStoneDmg = 0;
  for (let t = 1; t <= stT; t++) {
    const atTier = gear.stone ? { ...gear, stone: { ...gear.stone, t } } : gear;
    const stoneDmgAtTier = dev(S?.dmg, tierMul(t)) * supportBreakdownOf(atTier).tierTx;
    const axisAtTier = stoneAxis(S?.id, t);
    const orbitAtTier = S?.melee && axisAtTier.key === 'orbit' ? axisAtTier.v : 1;
    supportedStoneDmg = Math.max(supportedStoneDmg, stoneDmgAtTier);
    // 차돌은 돌 피해 등급과 궤도축을 피해에 다시 곱해 고등급에서 두 번 자라고 있었다.
    // ●1~●3의 기존 곡선은 보존하되, 기준점(orbit=1) 이후에는 궤도축이 대역 두께와
    // 회전 속도만 밀게 한다. 피해 자체도 stoneDmgAtTier로 계속 단조 증가한다.
    const orbitDamageAxis = S?.melee ? Math.min(1, orbitAtTier) : 1;
    supportedOrbitStoneDmg = Math.max(supportedOrbitStoneDmg, stoneDmgAtTier * orbitDamageAxis);
  }
  supportedStoneDmg *= support.classTx;
  supportedOrbitStoneDmg *= support.classTx;
  // 등급 배수 × 받쳐짐은 같은 짝에서 돌 등급이 오를수록 단조 증가한다. 원시 받쳐짐은
  // 낮은 돌일수록 높아질 수 있으므로, 화면 비교에는 이 실효 배율을 따로 내보낸다.
  const effectiveTierMul = sm * sup;

  // ── 등급이 무엇을 미는가 — **돌마다 다르다**(STONE_AXIS) ──────────
  const AX = stoneAxis(S?.id, stT);
  const ax = (k) => (AX.key === k ? AX.v : null);

  // ── 세트 — **4칸이 같은 수식어 + 최저 등급 4 이상** ─────────────────
  const SET = setOf(gear);
  const HYBRID = hybridSetOf(gear);
  // ◆ 세트는 「크게」를 교체하는 상태가 아니라 그 위에 쌓이는 단계다.
  // 따라서 기존 큰 세트 효과를 판정하는 모든 `big` 갈래는 ◆에서도 참이다.
  const big = SET?.level === 'large' || SET?.level === 'diamond';
  const diamond = SET?.level === 'diamond';
  const setIs = (id) => SET?.id === id;
  // 혼합 세트는 피해 배수가 아니라 유한 행동 파라미터만 낸다. `stage`는 양쪽
  // 2개 쌍 중 낮은 등급으로 이미 제한돼 있으므로 한 고등급 부품이 값을 끌지 못한다.
  const hybrid = HYBRID ? Object.freeze({
    hook: HYBRID.hook,
    stage: HYBRID.stage,
    edgeCount: HYBRID.stage,
    lineShardEvery: 7 - HYBRID.stage,
    impactEvery: 7 - HYBRID.stage,
    crossAngle: 0.08 + HYBRID.stage * 0.04,
    returnRetain: 0.48 + HYBRID.stage * 0.10,
    outerSeek: HYBRID.stage,
  }) : null;

  // ══════════════════════════════════════════════════════════════
  //  ⚠⚠ **세트 효과의 크기는 전부 【잠정】이다.**   【검증 2026-07-30】
  //    설계 E 가 정한 것은 **무엇이 극단으로 밀리는가**뿐이고 **얼마나**는 안 줬다.
  //    아래 여섯 자리(dry +1/+2 · loose ×0.5/0 · rough (d+1)/2 → 1 ·
  //    tamed 1/2 · aged ×2/0 · hard 1/2)는 그래서 **전부 실측 대기**다.
  //    ⚠ 직전 판 보고가 *"전부 개수·플래그로만 짰다 · 새 배율도 없다"* 라고 적었는데
  //      ×0.5 · ×2 · (d+1)/2 는 배율이다. 그 문장은 틀렸다 — 여기 표시로 바로잡는다.
  //    ⚠ 세트는 **회차에 한 번 볼까 말까**(실측 상한 3.3%) 하므로 이 여섯 값이 평시
  //      밸런스를 흔들지는 않는다. 그래서 지금은 **모양만 맞추고 크기는 미룬다.**
  // ══════════════════════════════════════════════════════════════

  // **개수는 받침 「종류」가 정한다.** 등급으로 개수가 흔들리면 예측이 안 되므로,
  // 등급은 물림 정도만 민다. 단 **원래 여러 개를 무는 받침만** ●●●●○ 이상에서
  // 하나 더 문다 — 하나만 꽉 무는 받침은 정체성이라 등급이 올라도 개수가 늘면 안 된다.
  // ⚠ `holdKeep` — **되돌아오는 돌(차돌)만** 무는 수. 묵은 받침의 C-1 예외가 여기서만
  //   산다. 무거운 돌은 `hold` 를 그대로 읽으므로 발 1 이고 착탄 광역이 안 곱해진다.
  // ⚠ 구 `S.keep && C.holdKeep` 갈래는 삭제됐다 — 차돌이 필드에 안 남으므로
  //   「차돌 둘을 문다」의 근거가 사라졌다(묵은 받침의 `holdKeep` 도 같이 지웠다).
  const rawHeld = (S && C) ? (S.small ? C.holdSmall : C.hold) : 1;
  // 수량은 연속값으로 둘 수 없다. 받침 급만큼 줄여 내림하고, 장착 자체는 항상 1발을
  // 보장한다. 그래서 보통급 `hold:2` 받침에 무거운 돌을 끼워 광역 두 발을 복제하는
  // 구멍은 닫히지만, 같은 받침+보통 돌의 기존 두 발은 그대로다.
  const held = Math.max(1, Math.floor(rawHeld * cradleGrip));
  let shots = Math.max(1, held + (ax('shots') ?? 0) + (held >= 2 && crT >= 4 ? 1 : 0));
  // 가벼운 돌의 첫 등급 부채 폭을 기준으로 삼는다. 이후 돌/받침 등급과 세트가 주는
  // 추가 발은 그 부채를 넓히는 단점이 아니라 기존 틈을 채우는 순이득이어야 한다.
  const fanBaseShots = S?.id === 'jandol'
    ? Math.max(1, held + stoneAxis('jandol', 1).v) : shots;
  // 마른 세트 — **발이 더 늘어난다.** 그 세트의 정체(수)를 극단으로 미는 것뿐이다.
  if (setIs('dry')) shots += big ? 2 : 1;                       // ⚠【잠정】
  // 가벼운 ◆ — 큰 세트의 +2를 유지하고 두 발만 더 얹는다.
  // ◆ 등급 자체가 이미 발 수를 올리므로 세트에서 다시 배로 튀지 않는다.
  if (diamond && setIs('dry')) shots += 2;

  // ── 성긴 고른 돌 — 「선」 ────────────────────────────────────────
  // 축은 연사이고 화면에 나타나는 것은 **발 간격**이다. gear 는 투사체 속도를
  // 모르므로 **간격 배율**(1 = 평범, 작을수록 촘촘)로 내보낸다.
  const rateAx = ax('rate') ?? 1;
  const gapBase = S?.pierceInf ? 1 / rateAx : 1;
  let lineGap = gapBase;
  // 성긴 세트 — **간격이 사라진다.** 0 = 돌이 붙어 진짜 선이 된다.
  if (setIs('loose') && S?.pierceInf) lineGap = big ? 0 : lineGap * 0.5;   // ⚠【잠정】
  // ── ⚠⚠ 「간격이 사라진다」를 **판정**으로 옮기는 유일한 자리 ──────────
  //   발 간격 = 투사체 속도 ÷ 초당 발수 이므로 **간격을 줄이는 것 = 연사를 올리는 것**이다.
  //   그런데 직전 단계 실측(ⓒ)이 잡아낸 대로 **연사 하드 상한 4발/초가 고른 돌의
  //   등급 축을 먹고 있다**(●●●●● 에서 원하는 4.83~5.56/초 → 실제 4.00, −17~−28%).
  //   즉 「간격」을 정하는 것은 등급이 아니라 그 상한이고, 세트가 그 상한을 못 풀면
  //   *"진짜 선이 된다"* 가 화면에서 아무 일도 안 하게 된다.
  //   → **성긴 세트일 때만 상한을 `간격이 줄어든 만큼` 푼다.** 새 상수가 없다 —
  //     배수 = 세트 전 간격 ÷ 세트 후 간격 (작게 = 2배, 크게 = 무제한).
  //   ⚠ 이것이 **`0.25초 하드 상한`을 건드리는 유일한 예외**다. 평시에는 한 톨도 안
  //     풀린다(세트가 아니면 배수 1). 상한 자체를 풀지 말지는 **사장님 판단 대기**이며,
  //     여기서는 「회차에 한 번」의 사건 안으로만 열어 둔다. 【잠정】
  //   ⚠⚠ **「작게」(●●●●○)는 판정에서 아무 일도 안 한다.**   【실측 2026-07-30】
  //     ●●●●○ 세트: 상한 4.00 → 8.00 으로 열지만 **원하는 연사가 3.10 이라 상한에
  //       애초에 안 닿는다** → 실제 3.10/초. 세트를 깨도 3.10/초. **차이 0.**
  //     ●●●●● 세트: 상한 4.00 → 4000(CAP_OPEN)으로 열고 원하는 4.83 이 상한에 걸려
  //       있었으므로 깬 것 4.00 → 발동 **4.83/초 (+21%)**. 여기서만 유효하다.
  //     즉 「작게」에서 「간격이 사라진다」가 움직이는 것은 `lineGap`(그림)뿐이다.
  //     ⚠ 이 주석은 *"판정으로 옮기는 유일한 자리"* 라고 단정했는데, **「작게」에서는
  //       판정이 아니라 그림만 움직인다.** 그 사실을 여기 남긴다 — 고치려면 「작게」의
  //       배수를 새로 정해야 하고 그것은 새 절대값이다(규율 7).
  //   ⚠⚠ **`Infinity` 를 쓰지 않는다.**   【검증 2026-07-30 — 잠재 사고】
  //     `JSON.stringify` 를 한 번만 타면 `Infinity → null` 이 되고, `mods.js` 의
  //     `cap = (1/0.25) * (m.rateCapMul ?? 1)` 에서 `4 * null = 0` → `Math.min(0, …)`
  //     → **발사가 통째로 멎는다.** 지금은 저장 경로가 없지만(day/autosave 가 gear/mods 를
  //     안 담는다) 값 하나가 그 경로를 한 번 타면 무기가 사라지는 것은 남겨 둘 위험이 아니다.
  //     → `CAP_OPEN` 은 **밸런스 값이 아니라 「문턱을 지나는 수」**다: 하드 상한(4발/초)의
  //       1000배라 어떤 빌드도 못 닿는다(전 조합 실측 최대 4.83발/초).
  const rateCapMul = (setIs('loose') && S?.pierceInf && lineGap !== gapBase)
    ? (lineGap > 0 ? gapBase / lineGap : CAP_OPEN) : 1;

  // ── 거친 납작한 돌 — 「물수제비」 ────────────────────────────────
  const skips = ax('skips') ?? 0;
  let skipDecay = skips ? SKIP.decay : 0;
  // 거친 세트 — **튐이 안 잦아든다.** 1 = 간격이 전혀 안 줄어든다.
  // ⚠⚠ **「◆32튐이 32배로 뻗는다」는 거짓이다.**   【실좌표 실측 2026-08-12】
  //   감쇠 1 이면 간격이 계속 93px 이라 25자국에 **2,325px** 가 필요한데 필드는
  //   1920×1080 이다. `world.skipOut`(SKIPW.outMargin 192px)이 필드를 벗어난 돌을 먼저
  //   치우므로 실제로 일어나는 것은 이렇다(N8·120초·seed 11·●●●●●):
  //     발당 자국  22.84 → **10.64 (−53%)**      ← 개수는 **반토막**
  //     발당 거리  17.75 → **28.27유닛 (+59%)**  ← 거리만 는다
  //   즉 세트가 하는 것은 「둘 다 늘리는 것」이 아니라 **개수를 거리로 바꾸는 것**이고,
  //   화력은 그래서 손해다(격리 자·N8·240초·2시드: **×0.961**, 3/3 시드 손해).
  //   ⚠ 부수 피해 — 필드 밖 사망은 `killShot` 을 직접 타므로 「빗나가면 굴러간다」·
  //     「맞고 튄다」가 **거의 안 발화한다**(실측 굴림 786→0 · 튐 103→0).
  //   → 고치는 길은 「감쇠를 1 이 아니라 하한으로 둔다」거나 「필드 안에서 예산을 다
  //     쓰게 총 이동을 사거리에 묶는다」인데 **둘 다 새 절대값**이라 여기서 안 정한다.
  //     E 가 요구한 *"자랑거리"* 가 지금은 순손해다 — 【사장님 판단 대기】.
  if (skips && setIs('rough')) skipDecay = big ? 1 : (skipDecay + 1) / 2;   // ⚠【잠정】

  // ── 묵은 차돌 — 「하나를 키운다」 ────────────────────────────────
  // ── 묵은 차돌 — 「도는 돌」 ──────────────────────────────────────
  //   축 하나가 반경·상한·주기를 같이 민다(ORB 주석). 차돌이 아니면 1 이라
  //   **다른 다섯은 이 갈래를 한 번도 안 탄다.**
  const orbAx = ax('orbit') ?? 1;
  const melee = !!S?.melee;
  const stonePierce = stonePierceValue(S);
  // **던지지 않는 돌은 상한이 없다** — 반경 안에 있으면 계속 맞는 것이 정체다
  const orbitCap = melee ? Infinity : 1;
  // 묵은 세트 — **도는 원이 커진다.** 반경이 곧 화력이므로 세트도 반경을 민다
  const orbSetR = (melee && setIs('aged')) ? ORB.setR[big ? 1 : 0] : 1;
  // **끈 등급이 반경을 민다** — 「엄청 증가」. `dev` 를 안 쓰는 이유는 ORB 주석에 있다
  const orbCordRaw = Math.pow(tierMul(gear.cord?.t ?? 1), ORB.cordPow);
  const orbCordT = melee
    ? Math.max(ORB.cordFloor, orbCordRaw > 1 ? Math.pow(orbCordRaw, cordGrip) : orbCordRaw)
    : 1;
  // **돌 등급이 궤적 대역의 두께를 민다** — 「돌에 해당하는 부분 히트박스가 커진다」
  const orbBand = melee ? ORB.bandBase * orbAx : 0;
  // 단단한 돌 1~4성은 넓은 무제한 타격 범위와 돌 피해가 너무 일찍 완성돼 있었다.
  // 새 수치를 만들지 않고 현재 차돌 축을 5성 축으로 나눈 **미완성 전달률**만 쓴다.
  //   피해  = early
  //   반경  = early^ORB.ratePow. 단, 세트가 처음 열리는 4성만 같은 축을 한 번 더
  //           전달하고 3성 전달률을 단조 바닥으로 둔다(chadolEarlyTransmission 정본).
  // 5성·◆는 분기 자체를 타지 않는다. 곱하기 1조차 하지 않아 기존 resolve 결과를
  // 바이트 단위로 보존하고, 작은 세트의 기존 반경 ×1.25도 그대로 남긴다.
  const chadolEarly = S?.id === 'chadol' ? chadolEarlyTransmission(stT) : null;
  const earlyChadol = chadolEarly !== null;
  const orbitRangeBase = devGrip(R?.range, rm, cordGrip) * orbSetR
    * (melee ? ORB.meleeR * orbCordT : 1);
  const orbitRange = earlyChadol
    ? orbitRangeBase * chadolEarly.range
    : orbitRangeBase;
  const orbitDamageBase = supportedOrbitStoneDmg * devGrip(L?.orbit, lm, loopGrip);

  // 묵은 세트 — **돌이 안 식는다.** 0 = 밤 내내 자란다.
  const baseSpreadMul = Math.max(SPREAD_MIN,
    Math.min(SPREAD_MAX,
      devLowGrip(C?.spread, cm, cradleGrip) * devLowGrip(R?.spread, rm, cordGrip)));
  const fanGapMul = S?.id === 'jandol' && shots > fanBaseShots && fanBaseShots > 1
    ? (fanBaseShots - 1) / (shots - 1) : 1;

  const out = {
    // ── 무기 본체 ────────────────────────────────────────────────
    // **받침도 발당 위력에 걸린다** — 꽉 물면 힘이 실린다(CRADLES 주석).
    dmgMul:   supportedStoneDmg * devGrip(C?.dmg, cm, cradleGrip),
    // ⚠ 고른 돌은 **기본 rate 가 평범(1.00)이고 등급 축이 연사를 민다.**
    //   걸이(연사)와 겹치지 않게 기본을 비워 둔 것이다(충돌 4).
    rateMul:  dev(S?.rate, sm) * rateAx * loopRateMul,
    // 돌의 고유 사거리는 원값을 고정한다. 손에 익은 돌의 등급축은 연사이고,
    // 사거리 성장은 끈만 담당하므로 `S.range`를 등급 지수에 다시 태우지 않는다.
    rangeMul: devGrip(R?.range, rm, cordGrip) * (S?.range ?? 1),
    shots,
    // 확산 — ⚠ **이제 받침·끈 둘이 만든다.** 걸이의 `aim` 이 궤도로 갔으므로
    //   구 `(2 − dev(L.aim))` 항이 사라졌다(D). 상·하한은 폭주 방지용이지 규칙이 아니다.
    // 가벼운 돌의 추가 발은 첫 등급 부채의 양끝을 더 벌리지 않고 그 사이를 채운다.
    // world의 전체 부채각은 `발 간격 × (발 수-1)`이므로 첫 등급/현재 간격 비를
    // 곱하면 새 절대각 없이 같은 폭을 정확히 보존한다. 그러지 않으면 등급·세트가
    // 올린 외곽 발이 화면 밖으로 새어 ‘발이 늘어남’이 오히려 단점이 된다.
    // SPREAD_MIN/MAX는 먼저 물리 부품의 원래 발 간격에 적용한다. 그 뒤 추가 발을
    // 기존 부채 안에 재배치하는 fanGapMul에는 다시 하한을 씌우지 않는다.
    spreadMul: baseSpreadMul * fanGapMul,
    // ⚠ **명중은 받침만 갖는다.** 걸이에서 뺐다(D).
    aimMul:    devGrip(C?.aim, cm, cradleGrip),

    // ── 돌의 성질 — **여섯이 여섯 가지 다른 질문에 답한다** ────────
    // ⚠⚠ **기본 광역 바닥은 「총량」이다 — 발마다 붙지 않는다.**
    //   처음엔 발당으로 깔았더니 발 수에 그대로 곱해졌다(잔돌 12발 = 48히트).
    //   실플레이에서 *"잔돌이 진짜 개사기"*(사장님)로 드러난 원인이 이것이다.
    //   분수는 world 의 `iPart()` 가 확률로 돌려 기대값을 보존한다.
    // 가벼운 돌은 부품·등급·세트가 결정한 최종 발 수의 공통 몫
    // `BASE_PIERCE / shots`를 그대로 보존하고, 각 발에 돌 고유 +1을 더한다.
    // 따라서 발마다 한 몸은 확정으로 뚫고 발사 묶음 전체의 공통 총량 1도 잃지 않는다.
    pierce:    (BASE_PIERCE / shots) + (stonePierce ?? 0),
    stonePierce: stonePierce ?? 0,                    // 상점·성능표가 같은 정본을 읽는다
    // 성긴 고른 돌 — **무한 관통.** `mods.pierceInf` 훅이 이미 있다(구 진화 자리)
    pierceInf: S?.pierceInf ? 1 : 0,
    lineGap,                                          // 선의 촘촘함(1 = 평범, 0 = 붙는다) — **scene 채널**
    rateCapMul,                                       // 연사 하드 상한 배수(성긴 세트만 ≠ 1)
    // 손에 익은 ◆ — 6번째 선만 0.10초 뒤 절반 위력으로 한 번 더 그린다.
    lineEchoEvery: diamond && setIs('loose') ? 6 : 0,
    lineEchoDmg:   diamond && setIs('loose') ? 0.50 : 0,
    lineEchoDelay: diamond && setIs('loose') ? 0.10 : 0,
    // 거친 납작한 돌 — **물수제비.** ⚠ 튐마다 광역을 붙이지 마라(반경 값이 없다)
    skips,
    skipGap:   skips ? SKIP.gap0 : 0,                 // 첫 간격(px). world 가 rangeMul 을 곱한다
    skipSeek:  skips ? SKIP.seekMin : 0,              // 적을 찾는 반경의 하한(px). 〃
    skipDecay,                                        // 등비. 다음 간격 = 이번 × decay
    // 매끈한 ◆ — 8번마다 적 탐색 반경만 두 배로 넓힌다.
    skipAnchorEvery: diamond && setIs('rough') ? 8 : 0,
    skipAnchorMul:   diamond && setIs('rough') ? 2 : 1,
    // 길든 모난 돌 — **파편.** 돌 고유값은 안 나눈다(그 돌의 정체성이므로)
    shards:    Math.max(BASE_SHARDS / Math.sqrt(shots), ax('shards') ?? 0),
    // 길든 세트 — **조각이 다시 조각을 낸다.** 0 = 안 깨진다(기본) · 몇 대(代)까지인가
    // ⚠ 한 조각이 내는 **자식은 하나**다(world.spawnShards). 조각 수만큼 또 내면
    //   13.6 → 185 → 2,500 으로 터진다 — 이 파일이 스스로 금지한 자리다. 【잠정】
    shardChain: setIs('tamed') ? (big ? 2 : 1) : 0,
    // 거친 ◆ — 연쇄 수나 세대를 늘리지 않고 마지막 조각만 남은 비행거리 안의 적을 본다.
    shardFinalSeek: diamond && setIs('tamed') ? 1 : 0,
    // 굳은 무거운 돌 — **착탄 광역**(유닛)
    splash:    ax('splash') ?? 0,
    // 굳은 세트 — **터진 자리가 또 터진다.** 0 = 한 번만(기본) · 몇 번 더인가
    // ⚠ 터지는 간격은 판정 상수라 `config.SETFX.againSec` 에 있다. 【잠정】
    splashAgain: setIs('hard') ? (big ? 2 : 1) : 0,
    // 무거운 ◆ — 기존 두 번의 재폭발 뒤에만 절반 위력의 바깥 고리 한 번.
    splashCrownDmg: diamond && setIs('hard') ? 0.50 : 0,
    splashCrownR:   diamond && setIs('hard') ? 1.18 : 1,
    // 묵은 차돌 — **필드 잔존 · 회수 · 성장.** 밤이 끝나면 리셋(상태는 world 가 갖는다)
    // 묵은 차돌 — **근접.** 성질이 「도는 돌」 하나이므로 여기 낼 것이 없다.
    //   축은 아래 회전 광역 세 줄(`orbitR`·`orbitRate`·`orbitCap`)로 나간다.

    // ── 회전 광역 「도는 돌이 친다」 ─────────────────────────────
    // **몸 옆에서 도는 물맷돌 자체가 판정을 갖는다.**
    //   반경 ← 끈(길수록 크게 돈다 — 사거리와 같은 물리값. 물리라서 안 옮긴다)
    //   주기 ← 돌 + 걸이(가벼우면·빨리 돌리면 자주 친다)
    //   피해 ← 돌 + **걸이**  ⚠ 걸이의 `aim` 이 여기로 왔다(D)
    // ⚠⚠ **`rateAx` 를 여기에도 곱한다.**   【검증 2026-07-30 — 회귀 수정】
    //   고른 돌은 기본 rate 를 1.38 → 1.00 으로 내리고 그만큼을 등급 축(rateAx)으로
    //   옮겼다(A-1). 그런데 여기만 rateAx 를 안 곱해서 **「주기 ← 돌」이 고른 돌에서만
    //   거짓**이 됐다 — 궤도 타격 빈도가 구 대비 t1 −18% · t3 −28% · t5 −42%.
    //   축이 rate 인 유일한 돌에서 축이 통째로 빠졌던 것이므로 되돌린다.
    // ⚠ **차돌 축(`orbAx`)이 반경과 주기에 같이 곱해진다** — 그것이 「근접캐」의 전부다.
    //   등급이 오르면 히트박스가 커지고(반경) 더 자주 치고(주기) 더 많이 친다(상한).
    // 반경 ← **끈**(「광역기 수준」). 1~4성 차돌만 5성까지의 미완성 전달률을 거친다.
    orbitR:    orbitRange,
    // 궤적 대역 두께 ← **돌**(반경 대비 비율).  0 이면 대역 없음 = 원 전체가 같은 피해
    orbitBand: orbBand,
    // 차돌 전용 주기(초). 0 이면 `ORBIT.tick` 을 쓴다 — 다른 다섯은 그대로다
    orbitTick: melee ? ORB.tick : 0,
    // **`dmgRatio` 를 안 탄다** — 던지지 않는 돌에는 「던지기보다 약해야」 할 이유가 없다
    orbitFull: melee && ORB.dmgFull ? 1 : 0,
    // 단단한 ◆ — 여섯 번째 틱에서만 대역 안쪽의 감쇠를 한 번 풀어 준다.
    orbitSweepEvery: diamond && setIs('aged') ? 6 : 0,
    orbitRate: dev(S?.rate, sm) * rateAx * loopRateMul * Math.pow(orbAx, ORB.ratePow),
    // 피해 ← 돌 + 걸이. 1~4성 차돌만 아직 완성되지 않은 돌 축만큼 한 번 더 전달한다.
    orbitDmg:  earlyChadol ? orbitDamageBase * chadolEarly.damage : orbitDamageBase,
    // 한 번에 몇 마리 — **배율**이다. world 가 `ORBIT.cap` 에 곱해 반올림한다.
    //   ⚠ 차돌은 **항상 `Infinity`**(던지지 않는 대신 반경 안을 전부 계속 친다).
    //     나머지 다섯은 1 = 기본 난간 4 그대로 — 그쪽 궤도는 덤이라 난간이 필요하다.
    orbitCap,
    // **던지지 않는 돌인가.** world 발사부가 이 값을 보고 투사체를 아예 안 만든다
    meleeOnly: melee,

    // ── 세트 ────────────────────────────────────────────────────
    // ⚠ 표시는 **색이 아니라 궤도 이펙트**로 한다(충돌 6). scene 소관.
    set:       SET,                                   // null 이면 발동 안 함
    setSeen:   setCount(gear),                        // 「몇 칸이 같은 수식어인가」
    hybridSet: HYBRID,
    hybrid,

    // ── 화면 채널 — **부품 4칸을 화면 4채널에 1:1로 건다** ──────────
    //   돌 → 크기 · 받침 → 개수 · 끈 → 궤도 반경 · 걸이 → 회전 속도
    // 등급은 그 위에 **색**으로 얹는다 — 종류는 모양, 등급은 색.
    tier:      stT,
    stoneId:   S?.id ?? null,
    tierBySlot: { stone: stT, cradle: crT, cord: cordT, loop: loopT },
    diamondSlots: SLOTS.filter(slot => (gear[slot]?.t ?? 1) >= TIER_DIAMOND),
    // ⚠ **등급이 화면에서도 커져야 한다.** 단점 축이 등급을 안 타므로(순이득 결정)
    //   크기를 무게만으로 잡으면 ●1과 ●5가 완전히 같아진다 —
    //   *"강해졌다는 느낌이 안 드는 게 큰 거 같아"*(사장님)의 정체가 이것이다.
    stoneSize: Math.max(0.55, Math.min(2.6, (S?.w ?? 1) * (1 + 0.13 * (stT - 1)))),
    spinMul:   loopRateMul * dev(S?.rate, sm)
      * Math.pow(1 + 0.09 * ((gear.loop?.t ?? 1) - 1), loopGrip),
    // ⚠ **판정이 커지면 그림도 커져야 한다.** 차돌은 판정 반경을 그대로 따라간다 —
    //   안 그러면 「손 옆에서 짧게 도는 그림」인데 화면 절반이 맞는다(거짓말).
    // 1~4성 차돌은 방금 줄인 실제 판정 반경을 그대로 그린다. 5성·◆는 조정 전
    // 연산 순서까지 보존해 resolve 전체의 byte-equivalence를 깨지 않는다.
    orbitLook: earlyChadol ? orbitRange
      : melee ? devGrip(R?.range, rm, cordGrip) * orbSetR * ORB.meleeR * orbCordT
                      : devGrip(R?.range, rm, cordGrip)
                        * Math.pow(1 + 0.10 * ((gear.cord?.t ?? 1) - 1), cordGrip),
    support:   sup,
    effectiveTierMul,
    traits:    {},
    traitDetails: SLOTS.flatMap(slot => traitDetails(gear[slot])),
  };

  // 부가 줄 — 4칸에서 모아 **합산**한다. 같은 줄이 겹치면 더해진다.
  for (const s of SLOTS) {
    for (const id of gear[s]?.traits ?? []) {
      const t = TRAIT_BY_ID[id];
      if (!t) continue;
      if (t.v && typeof t.v === 'object') {
        out.traits[t.hook] = { ...(out.traits[t.hook] ?? {}), ...t.v };
      } else {
        out.traits[t.hook] = (out.traits[t.hook] ?? 0) + t.v;
      }
    }
  }
  return out;
}

// ── 오버킬 이월 — **지웠다.** 차돌과 함께 죽었다 ───────────────────
// ⚠⚠ **`CARRY` 상수는 2026-07-30 에 삭제됐다.** 차돌의 `carry: true` 와 `ramp` 를
//   지운 순간(A-3) 「오버킬 이월」은 부품 경로에서 정체를 잃었고, 충돌 2 가
//   *"사장님 승인 — 깨끗이 지운다"* 로 못 박았다.
//   차돌의 정체는 **이월이 아니라 시간**이다 — 필드에 남고, 회수하면 커져 있다.
//
//   같이 지운 것(규율 5 — *"값을 없앨 때 그것을 읽던 코드도 같이 정리하라"*):
//     mods.js   `import … CARRY` · `m.overkill` · `m.spread` · `neutralMods` 의 두 키
//               · EFFECTS 의 `k_heavy.spread` · `k_press.spread`
//     world.js  `damageEnemy` 의 오버킬 연쇄 루프
//     gearui.js `if (m.carry)` 한 줄
//   ⚠ 남겼던 것 하나 — `world.evoProg.overkill` **누적 카운터**. 이월 기제가 아니라
//     **넘친 피해의 총량**이라, 그때는 `cards.js` 결③ 진화 조건이 그 수를 읽었다.
//     【2026-07-31】 **그것도 지웠다** — 읽던 진화가 카드 체계와 함께 사라졌다
//     (`world.js` 1629행). 그러니 지금 `world.evoProg` 는 **없다**.
//     ⚠ 이 줄이 「남는다」라고 적혀 있던 탓에 `gear_world_check` 가 08-01까지
//       그것을 찾다가 터졌다 — 주석이 늦으면 검사가 거짓말을 한다.
//
//   ⚠ 직전 판이 *"mods.js 가 정적 import 하므로 못 지운다"* 며 묘비로 남겼는데,
//     **작업 범위표 두 번째 줄이 `mods.js  훅 표`** 였다. 범위 제약이 아니라 오독이었다.

// ── 시작 장비 — **매 밤 똑같다** ──────────────────────────────────
// 빈 칸으로 시작하면 물맷돌이 안 돈다. 그리고 **출발선이 같아야 그 밤에 「뭐가
// 떴는지」가 읽힌다** — 밤의 차이는 시작이 아니라 주운 것에서 나온다.
// ⚠ **구 시작 장비와 「같은 부품」이다.** 이름만 바뀌었다:
//     고른 돌 / 얇은 가죽 → 성긴 받침 / 땋은 끈 → 거친 끈 / 가죽 고리 → 길든 고리
//   ⚠ 단 **고른 돌의 기본 연사가 1.38 → 1.00 으로 내려갔으므로**(A-1) 시작 화력이
//     실제로 내려간다. **그 폭은 −7.4% 다.**   【검증 2026-07-30 — 수치 정정】
//       구 연사 항 = dev(1.38, tierMul) = 1.38^0.6 = **1.2132**  ← 1.38 이 아니다
//       신 연사 항 = 1.38^(0.6²)        = **1.1229**
//       1.1229 ÷ 1.2132 = 0.9256 → **−7.4%**
//     ⚠ 직전 판은 여기 「구 1.38배 대비 −19%」라고 적었다. 그것은 **구 값을 등급 배수에
//       태우기 전의 원시 상수와 비교**한 것이라 자가 다르다. 실측이 이 정정을 뒷받침한다:
//       weaponStats rate 1.82 → 1.68(−7.7%) · fire_check 무강화 1215 → 1138킬(−6.3%).
//     반대쪽은 보고보다 크게 벌어진다 — ●●●●○ +13.4% · ●●●●● +46.7%.
//     *"●○○○○ 는 평범, 등급에서만 극단"* 이 설계이므로 의도된 모양이다.
const byId = (arr, id) => arr.find(p => p.id === id);
export function startGear() {
  return {
    stone:  { p: byId(STONES,  'goreun'), t: 1, traits: [] },   // 성긴 고른 돌 — 주운 돌
    cradle: { p: byId(CRADLES, 'loose'),  t: 1, traits: [] },   // 성긴 받침   — 낡은 가죽
    cord:   { p: byId(CORDS,   'rough'),  t: 1, traits: [] },   // 거친 끈     — 닳은 끈
    loop:   { p: byId(LOOPS,   'tamed'),  t: 1, traits: [] },   // 길든 고리   — 헐거운 고리
  };
}

// ── 등급 분포 — **바닥이 0이 아니다** ─────────────────────────────
// 완전 차단이 아니라 **언제든 뜰 수 있는데 초반엔 아주 드물다**(사장님 결정).
// **게이팅을 밤 시계에 건다. 레벨업 횟수가 아니다.**
// u = 밤 진행률 0~1. ⚠ 표 전체 측정 대기 — 모양만 확정이다.
const TIER_W = [
  //           ●      ●●     ●●●    ●●●●   ●●●●●
  { u: 0.00, w: [70, 25, 4.0, 0.8, 0.20] },
  { u: 0.25, w: [40, 38, 17, 4.0, 1.00] },
  { u: 0.50, w: [15, 33, 35, 14, 3.00] },
  { u: 0.75, w: [5, 20, 38, 28, 9.00] },
  { u: 1.00, w: [2, 10, 33, 37, 18.0] },
];
export function tierWeights(u) {
  const x = Math.max(0, Math.min(1, u));
  let a = TIER_W[0], b = TIER_W[TIER_W.length - 1];
  for (let i = 0; i < TIER_W.length - 1; i++) {
    if (x >= TIER_W[i].u && x <= TIER_W[i + 1].u) { a = TIER_W[i]; b = TIER_W[i + 1]; break; }
  }
  const k = b.u === a.u ? 0 : (x - a.u) / (b.u - a.u);
  return a.w.map((v, i) => v + (b.w[i] - v) * k);
}
export function rollTier(rnd, u) {
  const w = tierWeights(u);
  let s = w.reduce((p, c) => p + c, 0) * rnd();
  for (let i = 0; i < w.length; i++) { s -= w[i]; if (s <= 0) return i + 1; }
  return 1;
}

// ── 부품 생성 ─────────────────────────────────────────────────────
// ⚠ **【A-3】 상점은 지금 낀 것을 본다.** 모든 급을 살 수 있지만 완전 물림과 느슨한
//   물림은 같은 제시가 아니다. **급이 짝의 정체이므로 급으로 가중한다** — 막지 않고
//   완전 물림 쪽으로 기울이기만 한다.
// 같은 수식어가 2/4 또는 3/4까지 모였을 때만, 아직 다른 수식어인 **부족 슬롯**의
// 같은 `setId`를 제한적으로 밀어 준다. 이미 맞은 슬롯·1/4·완성 4/4에는 걸리지 않는다.
// 새 계수를 만들지 않는다: 기존 급 호환 가중 중 가장 작은 양의 값인 2.0
// (`stone`의 같은 급)을 그대로 재사용한다.
const SET_PROGRESS_WEIGHT = 2.0;
export function rollPart(rnd, slot, u, gear = null) {
  const pool = PARTS[slot];
  const want = gear?.stone?.p?.cls;
  const progress = setCount(gear);
  const fillSet = progress.n >= 2 && progress.n < SLOTS.length
    && gear?.[slot]?.p?.setId !== progress.id ? progress.id : null;
  const setWeight = (x) => (fillSet && x.setId === fillSet ? SET_PROGRESS_WEIGHT : 1);
  let p;
  if (want === undefined && !fillSet) p = pool[Math.floor(rnd() * pool.length)];
  else {
    const wts = pool.map(x => (want === undefined ? 1
      : slot === 'stone'
      ? (x.cls === want ? 2.0 : 1)          // 빌드를 유지하는 쪽으로 기운다
      : (x.cls >= want ? 2.4 : 0.7)) * setWeight(x));
    let tot = wts.reduce((a, b) => a + b, 0), r = rnd() * tot;
    p = pool[pool.length - 1];
    for (let i = 0; i < pool.length; i++) { r -= wts[i]; if (r <= 0) { p = pool[i]; break; } }
  }
  const t = rollTier(rnd, u);
  const traits = deterministicTraits(slot, p.id, t);
  return { slot, p, t, traits };
}

// ── 한 제시 = **네 부위 전부, 자리 고정** 【사장님 결정 2026-07-28】 ──────
// **구매 상한은 3회다.** 4칸이 열리면 **매 상점마다 반드시 하나를 버린다** —
// 이 재설계의 출발 진단이 *"12분 동안 포기한 것이 0이다"* 였다.
export function rollOffers(rnd, u, keep = null, gear = null) {
  return SLOTS.map(s => (keep && keep.slot === s) ? keep : rollPart(rnd, s, u, gear));
}

// ══════════════════════════════════════════════════════════════════
//  같은 부품을 또 사면 **등급이 오른다**   【사장님 지시 2026-08-01】
//
//  원문: *"똑같은 파츠를 사면 다음등급으로 업그레이드. (ex. 3등급 성긴 가죽끈 을
//         끼고 있는 상태에서 상점에서 3등급 성긴 가죽끈을 사면 4등급 성긴 가죽끈으로
//         업그레이드. 1-2-3-4 모두 적용.)"*  → 1→2 · 2→3 · 3→4 · 4→5 네 단계 전부.
//
//  ■ **종류와 등급이 둘 다 같을 때만** 걸린다. 사장님 확정: *"희귀한 잭팟인거지."*
//    그래서 **제시 확률은 손대지 않았다** — 상점이 이미 낀 부품을 배제하지 않으므로
//    지금 뽑기 그대로 가끔 겹치고, 겹치는 그 순간이 잭팟이다.
//    등급이 다르면(3 끼고 2 를 사면) **지금 그대로 그냥 갈아 낀다** — 내려가기도 한다.
//    5등급에서 같은 5등급을 다시 사면 재구매 전용 6성(◆)으로 오른다.
//    6성은 더 오르지 않고, 제시 자체에서는 절대로 직접 뽑히지 않는다.
//
//  ■ **특성은 끼고 있던 것이 따라 올라간다.** 등급이 요구하는 개수가 늘 때만
//    하나를 더 뽑는다. 사장님 지시: *"4-5 특성은 지금 있는 특성중에서 랜덤하게."*
//    → `TRAITS` 표에서 **이미 가진 것과 겹치지 않게** 고른다.
//    ⚠ 늘어나는 자리는 **둘**이다 — `traitCount` 가 `t>=5 ? 2 : t>=3 ? 1 : 0` 이므로
//      **2→3 에서 0개→1개**, **4→5 에서 1개→2개**. (처음에 4→5 하나뿐이라고 적었다가
//      실측에서 2→3 도 느는 것을 보고 고쳤다.) 같은 규칙이라 따로 갈라 쓰지 않는다 —
//      「그 등급의 부품이 원래 갖는 칸 수」를 채우는 것이고, 새로 뽑은 부품과 같다.
//
//  ■ ⚠ **경제는 안 건드렸다.** 값이 같은데 힘이 세지므로 분명히 세진다.
//    「경제는 마지막에 한 번에」(사장님 지시)라 **재서 적기만** 한다.
// ══════════════════════════════════════════════════════════════════

/** 지금 낀 것과 제시가 **같은 종류·같은 등급**이고 아직 다이아가 아닌가. */
export function isUpgrade(worn, part) {
  if (!worn?.p || !part?.p) return false;
  if (worn.p.id !== part.p.id) return false;          // 종류가 다르다
  if ((worn.t | 0) !== (part.t | 0)) return false;    // 등급이 다르다
  return (worn.t | 0) < TIER_DIAMOND;                 // ◆에서는 더 오르지 않는다
}

/**
 * 한 등급 올린 부품을 만든다. **원본은 안 건드린다.**
 * @param {object} worn  지금 낀 것 `{p,t,traits}`
 * @param {() => number} rnd  0~1 난수 — 늘어난 특성 칸을 채울 때만 쓴다
 */
function slotOfPart(p) {
  return SLOTS.find(slot => PARTS[slot].includes(p) || PARTS[slot].some(x => x.id === p?.id)) ?? null;
}

export function upgraded(worn, rnd) {
  const t = Math.min(TIER_DIAMOND, (worn.t | 0) + 1);
  const slot = slotOfPart(worn.p);
  const traits = slot ? deterministicTraits(slot, worn.p.id, t)
    : (worn.traits ?? []).slice(0, traitCount(t));
  return { p: worn.p, t, traits };
}

// ── 측정용 빌드 ───────────────────────────────────────────────────
// **모델이지 측정값이 아니다.** ⚠ **등급만 밤 진행률로 올리고 부품 종류는 시드로
// 고정한다** — 종류까지 매 순간 바꾸면 「무엇을 낀 빌드인가」가 정의되지 않는다.
export function pickKinds(rnd) {
  const g = {};
  for (const s of SLOTS) {
    const pool = PARTS[s];
    g[s] = { p: pool[Math.floor(rnd() * pool.length)], t: 1, traits: [] };
  }
  // 모든 급 조합이 장착되므로 뽑힌 돌을 조용히 낮추지 않는다. 회색박스도 실제 상점과
  // 같은 6^4 조합을 측정해야 느슨한 물림의 비용이 결과에 포함된다.
  return g;
}
export function rampGear(kinds, u) {
  const t = Math.max(1, Math.min(TIER_MAX, 1 + Math.round(Math.max(0, Math.min(1, u)) * 3)));
  const g = {};
  for (const s of SLOTS) g[s] = { p: kinds[s].p, t, traits: [] };
  return g;
}
export function maxGear(kinds) {
  const g = {};
  for (const s of SLOTS) g[s] = { p: kinds[s].p, t: TIER_MAX, traits: [] };
  return g;
}

// ── 명중 배율 → `hitPad`(px 가산) 변환 ────────────────────────────
// world 의 명중 여유는 **px 가산**이고 gear 는 배율을 낸다. ⚠ 기준값 측정 대기.
// ⚠ 이제 **받침만** 이 값을 만든다(걸이의 aim 은 궤도로 갔다 — D).
export const AIM_PAD_REF = 5.0;
export function aimPad(aimMul) { return (aimMul - 1) * AIM_PAD_REF; }

// ── 표시 ──────────────────────────────────────────────────────────
// **같은 이름에 점만 다르다.** 게임 용어(레어·에픽)는 화면에 안 올린다.
// ⚠ **범위를 벗어난 등급에 죽지 않는다** 【2026-08-01】. 구 판은 `repeat(-1)` 로
//   예외를 냈고, 그래서 등급 상한이 깨진 변이를 넣었을 때 **판정기가 판정을 내리기
//   전에 여기서 먼저 터졌다** — 검사가 「무엇이 틀렸는지」를 말할 기회를 잃는다.
//   이건 그리는 함수다. 잘못된 등급을 잡는 것은 `isUpgrade`·`tierMul` 의 일이다.
export function dots(t) {
  if (isDiamondTier(t)) return '◆';
  const n = Math.max(0, Math.min(TIER_MAX, t | 0));
  return '●'.repeat(n) + '○'.repeat(TIER_MAX - n);
}
export function partLabel(o) { return `${o.p.ko} ${dots(o.t)}`; }
